import {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ResearchContractCatalog from "./ResearchContractCatalog";
import ResearchReadiness from "./ResearchReadiness";
import ResearchObservations from "./ResearchObservations";

type SeriesSummary = {
  id: string;
  exchange: string;
  category: string;
  symbol: string;
  interval: string;
  source: string;
  sha256: string;
  bar_count: number;
  start_time_ms: number;
  end_time_ms: number;
};

type HistoricalDataset = SeriesSummary & {
  provider: string;
  original_filename: string | null;
  uploaded_at: string;
  note: string;
  evidence_tier: "author_canonical" | "source_anchored" | "experimental";
  provenance_ref: string;
  timezone_name: string;
  source_issues: string;
};

type AnalysisRun = {
  id: string; series_id: string; profile: string; cursor: number;
  known_at_ms: number; question: string; packet_sha256: string;
  status: string; response: { unknowns?: string[]; trade_permission?: string };
  created_at: string;
};

type AnalysisResponse = {
  id: string; run_id: string; provider: string; model_name: string;
  packet_sha256: string; created_at: string;
  response: {
    facts: string[]; interpretations: string[]; alternatives: string[];
    applied_rules: string[]; unknowns: string[];
    confidence: "low" | "medium" | "high" | "unknown";
  };
};

type DetectionSimulationReport = {
  sampled_prefixes: number; candidate_appearances: number;
  unique_candidates: number; trade_count: number; notice: string;
};

type PriceModeComparison = {
  high_low: DetectionSimulationReport;
  close: DetectionSimulationReport;
};

const evidenceTierLabel: Record<HistoricalDataset["evidence_tier"], string> = {
  author_canonical: "канон автора",
  source_anchored: "локально подтверждённый",
  experimental: "исследовательский",
};

type ServerDataFile = {
  path: string; size_bytes: number; provider: string | null; symbol: string | null;
  interval: string | null; exchange: string | null; category: string | null;
};

type Bar = {
  open_time_ms: number;
  close_time_ms: number;
  known_at_ms: number;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
};

type ReplayFrame = {
  series: SeriesSummary;
  cursor: number;
  total: number;
  bars: Bar[];
};

type AggregationProfile = "continuous_strict" | "session_observed";

type SeriesLineage = {
  config: {
    profile: AggregationProfile;
    quality: {
      emitted_buckets: number;
      dropped_incomplete_buckets: number;
      emitted_incomplete_buckets: number;
      minimum_bucket_coverage: number;
    };
  };
};

type Annotation = {
  id: string;
  series_id: string;
  bar_index: number;
  point_type: PointType;
  price: string;
  profile: string;
  status: string;
  note?: string;
  source_ref?: string | null;
  known_at_ms: number;
};

type PointType = "p1" | "p2" | "p3" | "p4" | "p5" | "p6";
type ConstructionPriceMode = "high_low" | "body" | "close";
type BreakoutPriceMode = "high_low" | "close";

const constructionPriceModeLabel: Record<ConstructionPriceMode, string> = {
  high_low: "экстремумы баров",
  body: "границы тел баров (исследовательский профиль)",
  close: "цены закрытия",
};

const breakoutPriceModeLabel: Record<BreakoutPriceMode, string> = {
  high_low: "по экстремумам баров",
  close: "по закрытию",
};

const directionLabel: Record<ModelAnnotation["direction"], string> = {
  up: "восходящая",
  down: "нисходящая",
  unknown: "не задано",
};

const researchStatusLabel: Record<string, string> = {
  candidate: "кандидат",
  confirmed: "подтверждено",
  invalidated: "опровергнуто",
  expired: "утратило актуальность",
  observed: "наблюдение завершено",
  blocked: "заблокировано",
  draft: "черновик",
  reviewed: "проверено",
  approved: "утверждено",
  insufficient: "недостаточно проверок",
  consistent: "согласовано",
  needs_adjudication: "требуется разбор расхождений",
  unqualified: "не подготовлено",
};

const researchEventLabel: Record<string, string> = {
  scenario_started: "наблюдение начато",
  trend_line_touch: "касание линии тренда",
  trend_line_break: "пробой линии тренда",
  correction_after_p4: "коррекция после p4",
  p4_level_break: "пробой уровня p4",
  invalidation: "достигнут уровень отмены",
  projection_100_hit: "достигнута проекция 100%",
  projection_200_hit: "достигнута проекция 200%",
  timeout: "лимит наблюдения",
  end_of_visible_history: "конец доступной истории",
  blocked: "наблюдение заблокировано",
  not_observed: "событие не наблюдалось",
};

const labelResearchStatus = (value: string) => researchStatusLabel[value] ?? "неизвестный статус";
const labelResearchEvent = (value: string) => researchEventLabel[value] ?? "неизвестное событие";

type ResearchPivotPoint = {
  point_type: PointType;
  pivot_kind: "high" | "low";
  bar_index: number;
  confirmation_bar_index: number;
  price: string;
  event_time_ms: number;
  known_at_ms: number;
};

type ResearchMRCandidate = {
  id: string;
  model_type: "MR";
  direction: "up" | "down";
  status: "candidate";
  profile: "research";
  method: "fragment_extreme_pivot_v2";
  price_mode: "high_low" | "close";
  fragment_start_bar_index: number;
  fragment_end_bar_index: number;
  known_at_ms: number;
  score: number;
  points: ResearchPivotPoint[];
  trace: string[];
};

type ModelAnnotation = {
  id: string;
  series_id: string;
  version: number;
  model_type: "MR" | "MP" | "MDR" | "unknown";
  direction: "up" | "down" | "unknown";
  status: "candidate" | "confirmed" | "invalidated" | "expired";
  profile: string;
  point_ids: string[];
  title: string;
  note: string;
  source_ref: string | null;
  known_at_ms: number;
};

type GoldenCase = {
  id: string;
  series_id: string;
  title: string;
  classification: "unclassified" | "positive" | "negative" | "boundary";
  expected_model_type: ModelAnnotation["model_type"];
  profile: string;
  review_status: "draft" | "reviewed" | "approved";
  start_bar_index: number;
  end_bar_index: number;
  model_ids: string[];
  tags: string[];
  source_ref: string | null;
  note: string;
};

type CaseReviewMetadata = {
  case_id: string; video_url: string | null; timecode_start: string | null;
  timecode_end: string | null; exchange: string | null; timeframe: string | null;
  price_mode: string | null; visible_until_ms: number | null;
  fact: string; interpretation: string; uncertainty: string;
};

type CaseReview = {
  id: string; case_id: string; reviewer: string; disposition: string;
  classification: GoldenCase["classification"]; points: Record<string, unknown>[];
  fact: string; interpretation: string; uncertainty: string; created_at: string;
};

type CaseReviewComparison = {
  case_id: string; review_count: number; unique_reviewer_count: number; classifications: Record<string, number>;
  dispositions: Record<string, number>; consensus: string;
  point_signatures: Record<string, string[]>; point_consensus: string;
};

type CaseReviewGate = {
  case_id: string;
  review_ready: boolean;
  canonical_ready: boolean;
  independent_reviewer_count: number;
  consensus: string;
  missing_requirements: string[];
  note: string;
  lifecycle_state: string;
  lifecycle_reason: string;
};

type DatasetQualityReport = {
  series_id: string;
  interval: string;
  bar_count: number;
  expected_interval_ms: number | null;
  chronology_violations: number;
  duplicate_open_times: number;
  known_at_violations: number;
  gap_count: number;
  largest_gap_ms: number;
  known_at_delay_min_ms: number | null;
  known_at_delay_max_ms: number | null;
  metadata_missing: string[];
  source_issues: string;
  notice: string;
};

type MarketCoverageRow = {
  series_id: string; symbol: string; interval: string; source: string; provider: string;
  evidence_tier: HistoricalDataset["evidence_tier"]; start_time_ms: number; end_time_ms: number;
  bar_count: number; origin: string; parent_series_id: string | null; gap_count: number;
  metadata_missing: string[]; source_issues: string;
};

type MarketCoverageReport = { generated_at: string; rows: MarketCoverageRow[]; counts: Record<string, number>; notice: string };
type TimeframeComparisonReport = {
  base_series_id: string; other_series_id: string; compared_interval: string; matched_bars: number;
  base_only_bars: number; other_only_bars: number; ohlc_mismatch_count: number;
  first_mismatch: { open_time_ms: number; fields: string[] } | null; notice: string;
};
type DataIssueReport = { counts: Record<string, number>; notice: string; issues: { code: string; severity: string; series_id: string; symbol: string; interval: string; message: string; action: string }[] };
type AggregationParityReport = { counts: Record<string, number>; notice: string; rows: { derived_series_id: string; symbol: string; interval: string; state: string; notice: string; comparison: TimeframeComparisonReport | null }[] };
type RefreshPlanReport = { notice: string; items: { series_id: string; symbol: string; interval: string; provider: string; action: string; reason: string }[] };
type InstrumentIdentity = { symbol: string; aliases: string[]; substitutions_forbidden: string[]; warnings: string[]; market_identity: string };
type CalendarProfile = { id: string; version: string; scope: string; timezone: string; aggregation_profile: string; description: string; limitations: string };
type VersionChainReport = { head_series_id: string; segments: string[]; composable: boolean; notice: string };
type ResearchUniverseReport = { counts: Record<string, number>; notice: string; rows: { symbol: string; expected_intervals: string[]; available_intervals: string[]; missing_intervals: string[]; providers: string[]; status: string; note: string }[] };

type ResearchHypothesis = {
  id: string;
  version: number;
  title: string;
  status: string;
  source_tier: string;
  source_refs: string[];
  trigger_event: string;
  observations: string[];
  invalidation_policy: string;
  block_reason: string;
  test_notes: string;
};

type ResearchScenarioRun = {
  id: string;
  series_id: string;
  model_id: string;
  hypothesis_id: string;
  profile: string;
  start_bar_index: number;
  end_bar_index: number;
  known_at_ms: number;
  status: string;
  config: { outcome?: { kind: string; bar_index: number | null; known_at_ms: number | null; note: string } };
  levels: { id: string; price: string; note: string }[];
  events: { kind: string; bar_index: number; known_at_ms: number; price: string | null; note: string }[];
};

type ResearchScenarioBatchResult = {
  series_id: string; hypothesis_id: string; requested_models: number; completed_runs: number;
  skipped_models: { model_id: string; reason: string }[]; summary: Record<string, number>;
  evaluation: {
    profile: string; trade_permission: string; method: string; split_fraction: number;
    groups: Record<string, { sample_count: number; outcomes: Record<string, number> }>;
    cost_model: string; limitations: string[];
  };
  runs: ResearchScenarioRun[];
};

type ResearchScenarioGlobalBatchResult = Omit<ResearchScenarioBatchResult, "series_id"> & {
  by_series: Record<string, { runs: number; outcomes: Record<string, number> }>;
};
type ExperimentReport = { hypothesis_id: string | null; run_count: number; evaluation: ResearchScenarioBatchResult["evaluation"]; reproducibility: Record<string, string>; rows: { run_id: string; symbol: string; interval: string; provider: string; outcome: { kind: string }; trade_permission: string }[]; notice: string };

type ModelResearchTrace = {
  model_id: string; series_id: string; state_machine_version: string; current_state: string;
  trade_permission: string;
  trace: { state: string; label: string; reason: string; known_at_ms: number; bar_index: number | null; event_kind: string | null }[];
};

const caseStatusLabel: Record<GoldenCase["review_status"], string> = {
  draft: "черновик", reviewed: "проверен", approved: "утверждён",
};

const caseClassificationLabel: Record<GoldenCase["classification"], string> = {
  unclassified: "без класса", positive: "положительный", negative: "отрицательный", boundary: "пограничный",
};
const reviewDispositionLabel: Record<string, string> = {
  independent: "независимая",
};
const reviewConsensusLabel: Record<string, string> = {
  insufficient: "недостаточно проверок",
  consistent: "согласовано",
  needs_adjudication: "требуется разбор расхождений",
};

type RuleDefinition = {
  id: string;
  version: number;
  profile: string;
  scope: string;
  title: string;
  statement: string;
  predicate: Record<string, unknown> | null;
  status: "confirmed" | "candidate" | "disputed" | "unknown" | "retired";
  confidence: string;
  sources: string[];
  exceptions: string;
  test_notes: string;
};

type OhlcMapping = {
  timestamp: string | null;
  date: string | null;
  time: string | null;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string | null;
  turnover: string | null;
  close_time: string | null;
  known_at: string | null;
};

type OhlcInspection = {
  has_header: boolean;
  encoding: string;
  delimiter: string;
  columns: string[];
  mapping: OhlcMapping | null;
  timestamp_unit: string;
  decimal_separator: string;
  inferred_interval: string | null;
  preview: Record<string, string>[];
  warnings: string[];
};

type User = {
  id: number;
  username: string;
  email: string | null;
  full_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type SimulationPolicyView = {
  account_scope: "per_instrument";
  initial_balance_usdt: string;
  fee_bps: string;
  slippage_bps: string;
  same_bar_resolution: "stop_first_conservative";
  max_open_positions: number;
  max_notional_usdt: string;
  max_risk_fraction: string;
  max_daily_loss_usdt: string;
  cooldown_after_rejections: number;
  max_data_staleness_seconds: number;
  stop_loss_mode: "structural_invalidation";
  connection_loss_action: string;
  email_notifications_enabled: boolean;
  telegram_notifications_enabled: boolean;
};

type SimulationProfile = {
  version: string;
  status: string;
  source: string;
  policy: SimulationPolicyView;
  assumptions_ru: string[];
  critical_events_ru: string[];
  notification_readiness_ru: { email: string; telegram: string };
  authorisation_gate_ru: string;
  trade_permission: string;
  simulation_permission: string;
  updated_by?: string;
  updated_at_utc?: string;
};

type KnowledgeEntry = {
  path: string;
  name: string;
  kind: "directory" | "file";
  size: number | null;
  modified_at: string;
  extension: string | null;
  preview_kind: "text" | "image" | "pdf" | "binary" | null;
};

type KnowledgePreview = {
  entry: KnowledgeEntry;
  content: string | null;
  truncated: boolean;
};

async function authorizedFetch(
  token: string,
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(input, { ...init, headers });
  if (response.status === 401) {
    window.dispatchEvent(new Event("adversa:unauthorized"));
  }
  return response;
}

const POINTS: PointType[] = ["p1", "p2", "p3", "p4", "p5", "p6"];
const SVG_WIDTH = 1000;
const SVG_HEIGHT = 520;
const PLOT = { left: 66, right: 22, top: 24, bottom: 58 };

function formatPrice(value: number): string {
  if (value >= 1000) return value.toFixed(1);
  if (value >= 10) return value.toFixed(2);
  return value.toPrecision(6);
}

function CandleChart({
  frame,
  annotations,
  models,
  researchCandidate,
  activePointId,
  onSelectPoint,
  onAddPoint,
  onMovePoint,
  onDeletePoint,
}: {
  frame: ReplayFrame;
  annotations: Annotation[];
  models: ModelAnnotation[];
  researchCandidate: ResearchMRCandidate | null;
  activePointId: string;
  onSelectPoint: (id: string) => void;
  onAddPoint: (pointType: PointType, barIndex: number, price: number) => void;
  onMovePoint: (
    annotation: Annotation,
    barIndex: number,
    price: number,
  ) => void;
  onDeletePoint: (annotation: Annotation) => void;
}) {
  const visible = frame.bars.slice(-120);
  const firstIndex = frame.bars.length - visible.length;
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    barIndex: number;
    price: number;
    annotationId: string;
  } | null>(null);
  const [draggingPointId, setDraggingPointId] = useState("");
  const [dragPreview, setDragPreview] = useState<{
    barIndex: number;
    price: number;
  } | null>(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const dragMoved = useRef(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const chart = useMemo(() => {
    const values = visible.flatMap((bar) => [
      Number(bar.high),
      Number(bar.low),
    ]);
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const padding = Math.max((rawMax - rawMin) * 0.08, rawMax * 0.0002);
    const min = rawMin - padding;
    const max = rawMax + padding;
    const width = SVG_WIDTH - PLOT.left - PLOT.right;
    const height = SVG_HEIGHT - PLOT.top - PLOT.bottom;
    const step = width / Math.max(visible.length, 1);
    const y = (price: number) =>
      PLOT.top + ((max - price) / Math.max(max - min, 1e-9)) * height;
    return { min, max, width, height, step, y };
  }, [visible]);

  useEffect(() => {
    if (!contextMenu) return;
    const dismiss = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        contextMenuRef.current?.contains(event.target)
      ) {
        return;
      }
      setContextMenu(null);
    };
    const dismissByKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") setContextMenu(null);
    };
    window.addEventListener("pointerdown", dismiss);
    window.addEventListener("keydown", dismissByKeyboard);
    return () => {
      window.removeEventListener("pointerdown", dismiss);
      window.removeEventListener("keydown", dismissByKeyboard);
    };
  }, [contextMenu]);

  const eventLocation = (clientX: number, clientY: number, bounds: DOMRect) => {
    if (!visible.length) return;
    const x = ((clientX - bounds.left) / bounds.width) * SVG_WIDTH;
    const y = ((clientY - bounds.top) / bounds.height) * SVG_HEIGHT;
    if (
      x < PLOT.left ||
      x > SVG_WIDTH - PLOT.right ||
      y < PLOT.top ||
      y > SVG_HEIGHT - PLOT.bottom
    ) {
      return;
    }
    const localIndex = Math.min(
      visible.length - 1,
      Math.max(0, Math.floor((x - PLOT.left) / chart.step)),
    );
    const price =
      chart.max -
      ((y - PLOT.top) / chart.height) * (chart.max - chart.min);
    return { barIndex: firstIndex + localIndex, price };
  };

  const pointIdFromTarget = (target: EventTarget | null) =>
    target instanceof Element
      ? target.closest<SVGGElement>("[data-point-id]")?.dataset.pointId ?? ""
      : "";

  const handleContextMenu = (event: ReactMouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    const bounds = event.currentTarget.getBoundingClientRect();
    const location = eventLocation(event.clientX, event.clientY, bounds);
    if (!location) {
      setContextMenu(null);
      return;
    }
    const annotationId = pointIdFromTarget(event.target);
    if (annotationId) onSelectPoint(annotationId);
    setContextMenu({
      x: Math.max(8, Math.min(event.clientX - bounds.left, bounds.width - 210)),
      y: Math.max(8, Math.min(event.clientY - bounds.top, bounds.height - 190)),
      ...location,
      annotationId,
    });
  };

  const handlePointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return;
    setContextMenu(null);
    const annotationId = pointIdFromTarget(event.target);
    if (!annotationId) {
      onSelectPoint("");
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    onSelectPoint(annotationId);
    setDraggingPointId(annotationId);
    setDragPreview(null);
    dragStart.current = { x: event.clientX, y: event.clientY };
    dragMoved.current = false;
  };

  const handlePointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!draggingPointId) return;
    if (
      !dragMoved.current &&
      Math.hypot(
        event.clientX - dragStart.current.x,
        event.clientY - dragStart.current.y,
      ) < 4
    ) {
      return;
    }
    dragMoved.current = true;
    const location = eventLocation(
      event.clientX,
      event.clientY,
      event.currentTarget.getBoundingClientRect(),
    );
    if (location) setDragPreview(location);
  };

  const finishDragging = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!draggingPointId) return;
    const annotation = annotations.find((item) => item.id === draggingPointId);
    const location =
      eventLocation(
        event.clientX,
        event.clientY,
        event.currentTarget.getBoundingClientRect(),
      ) ?? dragPreview;
    if (
      dragMoved.current &&
      annotation &&
      location &&
      (annotation.bar_index !== location.barIndex ||
        Number(annotation.price) !== location.price)
    ) {
      onMovePoint(annotation, location.barIndex, location.price);
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDraggingPointId("");
    setDragPreview(null);
    dragMoved.current = false;
  };

  const cancelDragging = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDraggingPointId("");
    setDragPreview(null);
    dragMoved.current = false;
  };

  const grid = Array.from({ length: 6 }, (_, index) => {
    const ratio = index / 5;
    const y = PLOT.top + ratio * chart.height;
    const price = chart.max - ratio * (chart.max - chart.min);
    return { y, price };
  });

  const contextAnnotation = contextMenu?.annotationId
    ? annotations.find((item) => item.id === contextMenu.annotationId)
    : undefined;

  return (
    <div className="chart-interaction">
      <svg
        className={`chart${draggingPointId ? " dragging-point" : ""}`}
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        role="img"
        aria-label="Покадровый свечной график"
        onContextMenu={handleContextMenu}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDragging}
        onPointerCancel={cancelDragging}
      >
      <rect width={SVG_WIDTH} height={SVG_HEIGHT} className="chart-bg" />
      {grid.map((line) => (
        <g key={line.y}>
          <line
            x1={PLOT.left}
            x2={SVG_WIDTH - PLOT.right}
            y1={line.y}
            y2={line.y}
            className="grid-line"
          />
          <text x={PLOT.left - 10} y={line.y + 4} className="axis-label">
            {formatPrice(line.price)}
          </text>
        </g>
      ))}
      {visible.map((bar, index) => {
        const open = Number(bar.open);
        const close = Number(bar.close);
        const high = Number(bar.high);
        const low = Number(bar.low);
        const x = PLOT.left + index * chart.step + chart.step / 2;
        const bodyTop = chart.y(Math.max(open, close));
        const bodyHeight = Math.max(Math.abs(chart.y(open) - chart.y(close)), 1);
        const up = close >= open;
        return (
          <g key={bar.open_time_ms} className={up ? "candle up" : "candle down"}>
            <line x1={x} x2={x} y1={chart.y(high)} y2={chart.y(low)} />
            <rect
              x={x - Math.max(chart.step * 0.3, 1)}
              y={bodyTop}
              width={Math.max(chart.step * 0.6, 2)}
              height={bodyHeight}
            />
          </g>
        );
      })}
      {models.map((model) => {
        const points = model.point_ids
          .map((id) => annotations.find((item) => item.id === id))
          .filter((item): item is Annotation => Boolean(item))
          .filter(
            (item) =>
              item.bar_index >= firstIndex &&
              item.bar_index < firstIndex + visible.length,
          );
        if (points.length < 2) return null;
        const coordinates = points
          .map((item) => {
            const localIndex = item.bar_index - firstIndex;
            const x = PLOT.left + localIndex * chart.step + chart.step / 2;
            return `${x},${chart.y(Number(item.price))}`;
          })
          .join(" ");
        return (
          <polyline
            key={model.id}
            points={coordinates}
            className={`model-line model-${model.status}`}
          />
        );
      })}
      {researchCandidate && (() => {
        const visiblePoints = researchCandidate.points.filter(
          (item) =>
            item.bar_index >= firstIndex &&
            item.bar_index < firstIndex + visible.length,
        );
        if (visiblePoints.length < 2) return null;
        const coordinates = visiblePoints.map((item) => {
          const localIndex = item.bar_index - firstIndex;
          const x = PLOT.left + localIndex * chart.step + chart.step / 2;
          return `${x},${chart.y(Number(item.price))}`;
        }).join(" ");
        return (
          <g className="research-candidate-overlay">
            <polyline points={coordinates} className="research-model-line" />
            {visiblePoints.map((item) => {
              const localIndex = item.bar_index - firstIndex;
              const x = PLOT.left + localIndex * chart.step + chart.step / 2;
              const y = chart.y(Number(item.price));
              return (
                <g key={`${researchCandidate.id}-${item.point_type}`}>
                  <circle cx={x} cy={y} r={10} />
                  <text x={x} y={y + 4}>{item.point_type.slice(1)}</text>
                </g>
              );
            })}
          </g>
        );
      })()}
      {annotations
        .filter(
          (item) =>
            item.bar_index >= firstIndex &&
            item.bar_index < firstIndex + visible.length,
        )
        .map((item) => {
          const preview =
            draggingPointId === item.id ? dragPreview : null;
          const displayedBarIndex = preview?.barIndex ?? item.bar_index;
          const displayedPrice = preview?.price ?? Number(item.price);
          const localIndex = displayedBarIndex - firstIndex;
          const x = PLOT.left + localIndex * chart.step + chart.step / 2;
          const y = chart.y(displayedPrice);
          return (
            <g
              key={item.id}
              data-point-id={item.id}
              className={[
                "point",
                `point-${item.point_type}`,
                activePointId === item.id ? "point-active" : "",
                draggingPointId === item.id ? "point-dragging" : "",
              ].filter(Boolean).join(" ")}
            >
              <circle cx={x} cy={y} r={12} />
              <text x={x} y={y + 4}>
                {item.point_type.slice(1)}
              </text>
            </g>
          );
        })}
      <text x={PLOT.left} y={SVG_HEIGHT - 20} className="chart-hint">
        ПКМ — добавить/удалить · перетащите точку мышью · Delete — удалить
      </text>
      <text
        x={SVG_WIDTH - PLOT.right}
        y={SVG_HEIGHT - 20}
        textAnchor="end"
        className="chart-hint"
      >
        видимые бары: {firstIndex + 1}–{frame.cursor + 1}
      </text>
      </svg>
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="chart-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          role="menu"
          onContextMenu={(event) => event.preventDefault()}
        >
          {contextAnnotation ? (
            <>
              <strong>{contextAnnotation.point_type.toUpperCase()}</strong>
              <button
                className="danger"
                role="menuitem"
                onClick={() => {
                  onDeletePoint(contextAnnotation);
                  setContextMenu(null);
                }}
              >
                Удалить точку
                <kbd>Del</kbd>
              </button>
            </>
          ) : (
            <>
              <strong>Добавить точку</strong>
              <div className="context-point-grid">
                {POINTS.map((point) => (
                  <button
                    key={point}
                    role="menuitem"
                    onClick={() => {
                      onAddPoint(
                        point,
                        contextMenu.barIndex,
                        contextMenu.price,
                      );
                      setContextMenu(null);
                    }}
                  >
                    {point.toUpperCase()}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const REQUIRED_OHLC_FIELDS = ["open", "high", "low", "close"] as const;
const OPTIONAL_OHLC_FIELDS = [
  "timestamp",
  "date",
  "time",
  "volume",
  "turnover",
  "close_time",
  "known_at",
] as const;

function utf8Base64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
}

function OhlcImportDialog({
  token,
  file,
  inspection,
  onClose,
  onImported,
  historyMode = false,
}: {
  token: string;
  file: File;
  inspection: OhlcInspection;
  onClose: () => void;
  onImported: (series: SeriesSummary) => void;
  historyMode?: boolean;
}) {
  const fallback = {
    timestamp: null,
    date: null,
    time: null,
    open: "",
    high: "",
    low: "",
    close: "",
    volume: null,
    turnover: null,
    close_time: null,
    known_at: null,
  };
  const [detected, setDetected] = useState(inspection);
  const [mapping, setMapping] = useState<OhlcMapping>(
    inspection.mapping ?? fallback,
  );
  const [symbol, setSymbol] = useState(
    file.name.replace(/\.[^.]+$/, "").replace(/_/g, "/").toUpperCase(),
  );
  const [exchange, setExchange] = useState("local");
  const [provider, setProvider] = useState(() => {
    const name = file.name.toLowerCase();
    return ["coinbase", "stooq", "bybit"].find((item) => name.includes(item)) ?? "manual";
  });
  const [category, setCategory] = useState("research");
  const [evidenceTier, setEvidenceTier] = useState<HistoricalDataset["evidence_tier"]>("experimental");
  const [provenanceRef, setProvenanceRef] = useState("");
  const [sourceIssues, setSourceIssues] = useState("");
  const [interval, setInterval] = useState(
    inspection.inferred_interval ?? "auto",
  );
  const [timezone, setTimezone] = useState("UTC");
  const [hasHeader, setHasHeader] = useState(inspection.has_header);
  const [delimiter, setDelimiter] = useState(inspection.delimiter);
  const [encoding, setEncoding] = useState(inspection.encoding);
  const [decimalSeparator, setDecimalSeparator] = useState(
    inspection.decimal_separator,
  );
  const [timestampUnit, setTimestampUnit] = useState(
    inspection.timestamp_unit,
  );
  const [datetimeFormat, setDatetimeFormat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(inspection.warnings.join(" · "));

  const changeHeaderMode = async (next: boolean) => {
    setHasHeader(next);
    setError("");
    const response = await authorizedFetch(
      token,
      `${historyMode ? "/api/history" : "/api/replay/series"}/import/file/inspect?has_header=${next}`,
      {
        method: "POST",
        headers: { "content-type": "application/octet-stream" },
        body: file,
      },
    );
    if (!response.ok) {
      setError("Не удалось повторно проанализировать структуру файла");
      return;
    }
    const result = (await response.json()) as OhlcInspection;
    setDetected(result);
    setMapping(result.mapping ?? fallback);
  };

  const setColumn = (field: keyof OhlcMapping, value: string) => {
    setMapping((current) => ({
      ...current,
      [field]: value || null,
    }));
  };

  const submit = async () => {
    if (
      !symbol.trim() ||
      REQUIRED_OHLC_FIELDS.some((field) => !mapping[field]) ||
      (!mapping.timestamp && !mapping.date)
    ) {
      setError("Укажите инструмент, время и обязательные OHLC-колонки");
      return;
    }
    setBusy(true);
    setError("");
    const config = {
      has_header: hasHeader,
      delimiter,
      encoding,
      decimal_separator: decimalSeparator,
      timestamp_unit: timestampUnit,
      datetime_format: datetimeFormat || null,
      timezone,
      mapping,
    };
    const params = new URLSearchParams({
      symbol: symbol.trim(),
      exchange: exchange.trim() || "local",
      category: category.trim() || "research",
      interval: interval.trim() || "auto",
    });
    if (historyMode) {
      params.set("provider", provider.trim() || "manual");
      params.set("filename", file.name);
      params.set("evidence_tier", evidenceTier);
      params.set("provenance_ref", provenanceRef.trim());
      params.set("timezone_name", timezone.trim() || "UTC");
      params.set("source_issues", sourceIssues.trim());
    }
    try {
      const response = await authorizedFetch(
        token,
        `${historyMode ? "/api/history" : "/api/replay/series"}/import/file?${params}`,
        {
          method: "POST",
          headers: {
            "content-type": "application/octet-stream",
            "X-OHLC-Config-Base64": utf8Base64(JSON.stringify(config)),
          },
          body: file,
        },
      );
      if (!response.ok) {
        const payload = (await response.json()) as { detail?: string };
        throw new Error(payload.detail || `HTTP ${response.status}`);
      }
      onImported((await response.json()) as SeriesSummary);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Ошибка импорта");
      setBusy(false);
    }
  };

  const columnSelect = (
    field: keyof OhlcMapping,
    label: string,
    required = false,
  ) => (
    <label>
      {label}
      <select
        value={mapping[field] ?? ""}
        required={required}
        onChange={(event) => setColumn(field, event.target.value)}
      >
        <option value="">— не используется —</option>
        {detected.columns.map((column) => (
          <option key={column} value={column}>{column}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="modal-backdrop">
      <section className="import-modal">
        <header>
          <div>
            <small>Универсальный OHLC-импорт</small>
            <h2>{file.name}</h2>
          </div>
          <button onClick={onClose}>Закрыть</button>
        </header>
        <div className="import-grid">
          <fieldset>
            <legend>Инструмент</legend>
            <label>Торговая пара / тикер
              <input value={symbol} onChange={(event) => setSymbol(event.target.value)} />
            </label>
            <label>Биржа / источник
              <input value={exchange} onChange={(event) => setExchange(event.target.value)} />
            </label>
            {historyMode && (
              <>
                <label>Источник данных
                  <input
                    value={provider}
                    placeholder="coinbase, stooq, bybit…"
                    onChange={(event) => setProvider(event.target.value)}
                  />
                </label>
                <label>Уровень доказательности
                  <select value={evidenceTier} onChange={(event) => setEvidenceTier(event.target.value as HistoricalDataset["evidence_tier"])}>
                    <option value="author_canonical">канон автора</option>
                    <option value="source_anchored">локально подтверждённый</option>
                    <option value="experimental">исследовательский</option>
                  </select>
                </label>
                <label>Ссылка на происхождение
                  <input value={provenanceRef} placeholder="архив, урок, URL, хеш…" onChange={(event) => setProvenanceRef(event.target.value)} />
                </label>
                <label>Неопределённости источника
                  <input value={sourceIssues} placeholder="например: provider_unknown" onChange={(event) => setSourceIssues(event.target.value)} />
                </label>
              </>
            )}
            <label>Категория рынка
              <input value={category} onChange={(event) => setCategory(event.target.value)} />
            </label>
            <label>Интервал
              <input
                value={interval}
                placeholder="auto, 1, 5, 1h, D"
                onChange={(event) => setInterval(event.target.value)}
              />
            </label>
            <label>Часовой пояс исходных дат
              <input
                value={timezone}
                placeholder="UTC, Europe/Moscow, +05:00"
                onChange={(event) => setTimezone(event.target.value)}
              />
            </label>
          </fieldset>
          <fieldset>
            <legend>Формат файла</legend>
            <label>Разделитель
              <select value={delimiter} onChange={(event) => setDelimiter(event.target.value)}>
                <option value=",">запятая</option>
                <option value=";">точка с запятой</option>
                <option value="\t">табуляция</option>
                <option value="|">вертикальная черта</option>
              </select>
            </label>
            <label>Первая строка
              <select
                value={hasHeader ? "header" : "data"}
                onChange={(event) =>
                  void changeHeaderMode(event.target.value === "header")
                }
              >
                <option value="header">заголовки колонок</option>
                <option value="data">сразу данные</option>
              </select>
            </label>
            <label>Кодировка
              <select value={encoding} onChange={(event) => setEncoding(event.target.value)}>
                <option value="utf-8-sig">UTF-8</option>
                <option value="utf-16">UTF-16</option>
                <option value="cp1251">Windows-1251</option>
              </select>
            </label>
            <label>Десятичный разделитель
              <select
                value={decimalSeparator}
                onChange={(event) => setDecimalSeparator(event.target.value)}
              >
                <option value="auto">автоматически</option>
                <option value=".">точка</option>
                <option value=",">запятая</option>
              </select>
            </label>
            <label>Единица Unix-времени
              <select value={timestampUnit} onChange={(event) => setTimestampUnit(event.target.value)}>
                <option value="auto">автоматически / дата</option>
                <option value="s">секунды</option>
                <option value="ms">миллисекунды</option>
                <option value="us">микросекунды</option>
                <option value="ns">наносекунды</option>
              </select>
            </label>
            <label>Формат даты (необязательно)
              <input
                value={datetimeFormat}
                placeholder="%Y-%m-%d %H:%M:%S"
                onChange={(event) => setDatetimeFormat(event.target.value)}
              />
            </label>
          </fieldset>
          <fieldset className="column-mapping">
            <legend>Соответствие колонок</legend>
            {columnSelect("timestamp", "Дата и время")}
            {columnSelect("date", "Дата отдельно")}
            {columnSelect("time", "Время отдельно")}
            {REQUIRED_OHLC_FIELDS.map((field) =>
              columnSelect(field, field.toUpperCase(), true),
            )}
            {OPTIONAL_OHLC_FIELDS.slice(3).map((field) =>
              columnSelect(field, field.replace("_", " ")),
            )}
          </fieldset>
        </div>
        <div className="import-preview">
          <table>
            <thead>
              <tr>
                {detected.columns.slice(0, 8).map((column) => (
                  <th key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detected.preview.slice(0, 3).map((row, index) => (
                <tr key={index}>
                  {detected.columns.slice(0, 8).map((column) => (
                    <td key={column}>{row[column]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {error && <p className="import-error">{error}</p>}
        <footer>
          <span>
            Автодетект: {detected.inferred_interval ?? "интервал не определён"}
          </span>
          <button className="primary" onClick={submit} disabled={busy}>
            {busy ? "Импорт…" : "Импортировать"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function Lab({
  token,
  currentUser,
  onLogout,
  onOpenUsers,
  onOpenKnowledge,
  onOpenHistory,
  onOpenRules,
  onOpenResearchContracts,
  onOpenResearchReadiness,
  onOpenResearchObservations,
  onOpenSimulationSettings,
}: {
  token: string;
  currentUser: User;
  onLogout: () => void;
  onOpenUsers: () => void;
  onOpenKnowledge: () => void;
  onOpenHistory: () => void;
  onOpenRules: () => void;
  onOpenResearchContracts: () => void;
  onOpenResearchReadiness: () => void;
  onOpenResearchObservations: () => void;
  onOpenSimulationSettings: () => void;
}) {
  const [series, setSeries] = useState<SeriesSummary[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [cursor, setCursor] = useState(0);
  const [frame, setFrame] = useState<ReplayFrame | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [models, setModels] = useState<ModelAnnotation[]>([]);
  const [cases, setCases] = useState<GoldenCase[]>([]);
  const [blindCaseId, setBlindCaseId] = useState("");
  const [selectedPointIds, setSelectedPointIds] = useState<string[]>([]);
  const [activePointId, setActivePointId] = useState("");
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("Загрузите набор Bybit, Binance или CSV");
  const [menuOpen, setMenuOpen] = useState(false);
  const [bybitSymbol, setBybitSymbol] = useState("BTCUSDT");
  const [bybitCategory, setBybitCategory] = useState("linear");
  const [bybitInterval, setBybitInterval] = useState("1");
  const [binanceSymbol, setBinanceSymbol] = useState("BTCUSDT");
  const [binanceInterval, setBinanceInterval] = useState("1w");
  const [binanceStart, setBinanceStart] = useState("");
  const [binanceEnd, setBinanceEnd] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [inspection, setInspection] = useState<OhlcInspection | null>(null);
  const [modelTitle, setModelTitle] = useState("");
  const [modelSourceRef, setModelSourceRef] = useState("");
  const [modelType, setModelType] = useState<ModelAnnotation["model_type"]>("MR");
  const [modelDirection, setModelDirection] =
    useState<ModelAnnotation["direction"]>("unknown");
  const [manualPriceMode, setManualPriceMode] =
    useState<ConstructionPriceMode>("high_low");
  const [breakoutPriceMode, setBreakoutPriceMode] =
    useState<BreakoutPriceMode>("high_low");
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
  const [caseTitle, setCaseTitle] = useState("");
  const [caseClassification, setCaseClassification] =
    useState<GoldenCase["classification"]>("unclassified");
  const [caseStart, setCaseStart] = useState(0);
  const [caseEnd, setCaseEnd] = useState(0);
  const [caseSource, setCaseSource] = useState("");
  const [caseTags, setCaseTags] = useState("mr");
  const [caseNote, setCaseNote] = useState("");
  const [metadataCaseId, setMetadataCaseId] = useState("");
  const [caseMetadata, setCaseMetadata] = useState<CaseReviewMetadata>({ case_id: "", video_url: null, timecode_start: null, timecode_end: null, exchange: null, timeframe: null, price_mode: null, visible_until_ms: null, fact: "", interpretation: "", uncertainty: "" });
  const [reviews, setReviews] = useState<CaseReview[]>([]);
  const [comparison, setComparison] = useState<CaseReviewComparison | null>(null);
  const [caseGate, setCaseGate] = useState<CaseReviewGate | null>(null);
  const [reviewer, setReviewer] = useState("");
  const [reviewFact, setReviewFact] = useState("");
  const [reviewInterpretation, setReviewInterpretation] = useState("");
  const [reviewUncertainty, setReviewUncertainty] = useState("");
  const [reviewPoints, setReviewPoints] = useState("[]");
  const [caseSourceDraft, setCaseSourceDraft] = useState("");
  const [reviewClassification, setReviewClassification] = useState<GoldenCase["classification"]>("unclassified");
  const [aggregateInterval, setAggregateInterval] = useState("60");
  const [aggregateTimezone, setAggregateTimezone] = useState("UTC");
  const [aggregateSession, setAggregateSession] = useState("00:00");
  const [aggregateProfile, setAggregateProfile] =
    useState<AggregationProfile>("continuous_strict");
  const [aggregateCalendar, setAggregateCalendar] = useState("moex_fx");
  const [aggregateCalendarVersion, setAggregateCalendarVersion] =
    useState("2026-06-22");
  const [researchCandidates, setResearchCandidates] =
    useState<ResearchMRCandidate[]>([]);
  const [selectedResearchId, setSelectedResearchId] = useState("");
  const [pivotWindow, setPivotWindow] = useState(3);
  const [minimumSwing, setMinimumSwing] = useState("0.1");
  const [researchPriceMode, setResearchPriceMode] =
    useState<"high_low" | "close">("high_low");
  const [activeProfile, setActiveProfile] = useState<HistoricalDataset["evidence_tier"]>("author_canonical");
  const [analysisQuestion, setAnalysisQuestion] = useState("");
  const [analysisRun, setAnalysisRun] = useState<AnalysisRun | null>(null);
  const [analysisResponses, setAnalysisResponses] = useState<AnalysisResponse[]>([]);
  const [simulationReport, setSimulationReport] = useState<DetectionSimulationReport | null>(null);
  const [priceModeComparison, setPriceModeComparison] = useState<PriceModeComparison | null>(null);
  const [researchHypotheses, setResearchHypotheses] = useState<ResearchHypothesis[]>([]);
  const [scenarioHypothesisId, setScenarioHypothesisId] = useState("P4-LT-V1");
  const [scenarioModelId, setScenarioModelId] = useState("");
  const [scenarioTimeout, setScenarioTimeout] = useState(120);
  const [scenarioSplitPercent, setScenarioSplitPercent] = useState(70);
  const [scenarioRun, setScenarioRun] = useState<ResearchScenarioRun | null>(null);
  const [scenarioBatchResult, setScenarioBatchResult] = useState<ResearchScenarioBatchResult | null>(null);
  const [scenarioGlobalBatchResult, setScenarioGlobalBatchResult] = useState<ResearchScenarioGlobalBatchResult | null>(null);
  const [experimentReport, setExperimentReport] = useState<ExperimentReport | null>(null);
  const [modelTrace, setModelTrace] = useState<ModelResearchTrace | null>(null);

  const loadSeries = useCallback(async () => {
    const response = await authorizedFetch(token, "/api/replay/series");
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = (await response.json()) as SeriesSummary[];
    setSeries(payload);
    if (!selectedId && payload.length) {
      setSelectedId(payload[0].id);
      setCursor(Math.min(99, payload[0].bar_count - 1));
    }
  }, [selectedId, token]);

  useEffect(() => {
    loadSeries().catch(() => setMessage("API недоступен"));
  }, [loadSeries]);

  useEffect(() => {
    if (!selectedId) return;
    const controller = new AbortController();
    Promise.all([
      authorizedFetch(token, blindCaseId ? `/api/replay/series/${selectedId}/cases/${blindCaseId}/blind` : `/api/replay/series/${selectedId}?cursor=${cursor}`, {
        signal: controller.signal,
      }).then((response) => response.json() as Promise<ReplayFrame>),
      authorizedFetch(token, `/api/replay/series/${selectedId}/annotations`, {
        signal: controller.signal,
      }).then((response) => response.json() as Promise<Annotation[]>),
      authorizedFetch(token, `/api/replay/series/${selectedId}/models`, {
        signal: controller.signal,
      }).then((response) => response.json() as Promise<ModelAnnotation[]>),
      authorizedFetch(token, `/api/replay/series/${selectedId}/cases`, {
        signal: controller.signal,
      }).then((response) => response.json() as Promise<GoldenCase[]>),
    ])
      .then(([nextFrame, nextAnnotations, nextModels, nextCases]) => {
        setFrame(nextFrame);
        setAnnotations(nextAnnotations);
        setModels(nextModels);
        setCases(nextCases);
        setSelectedPointIds([]);
        setActivePointId("");
        setSelectedModelIds([]);
        setResearchCandidates([]);
        setSelectedResearchId("");
        setMessage("Закрытые бары · без просмотра будущего");
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setMessage("Не удалось загрузить replay");
        }
      });
    return () => controller.abort();
  }, [blindCaseId, selectedId, cursor, token]);

  useEffect(() => {
    if (!playing || !frame) return;
    if (cursor >= frame.total - 1) {
      setPlaying(false);
      return;
    }
    const timer = window.setInterval(
      () => setCursor((current) => Math.min(current + 1, frame.total - 1)),
      450,
    );
    return () => window.clearInterval(timer);
  }, [playing, cursor, frame]);

  useEffect(() => {
    authorizedFetch(token, "/api/analysis/hypotheses")
      .then(async (response) => {
        if (!response.ok) throw new Error("Не удалось загрузить гипотезы");
        setResearchHypotheses((await response.json()) as ResearchHypothesis[]);
      })
      .catch(() => setMessage("Не удалось загрузить реестр исследовательских гипотез"));
  }, [token]);

  useEffect(() => {
    if (!selectedId || !scenarioModelId) {
      setModelTrace(null);
      return;
    }
    authorizedFetch(token, `/api/analysis/models/${scenarioModelId}/trace?series_id=${selectedId}`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Не удалось получить trace модели");
        setModelTrace((await response.json()) as ModelResearchTrace);
      })
      .catch(() => setModelTrace(null));
  }, [scenarioModelId, selectedId, token]);

  const importBybit = async () => {
    setBusy(true);
    setMessage("Загрузка закрытых свечей Bybit…");
    try {
      const response = await authorizedFetch(token, "/api/replay/series/import/bybit", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          category: bybitCategory,
          symbol: bybitSymbol.trim().toUpperCase(),
          interval: bybitInterval,
          limit: 500,
          closed_only: true,
        }),
      });
      if (!response.ok) {
        const error = (await response.json()) as { detail?: string };
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      const imported = (await response.json()) as SeriesSummary;
      await loadSeries();
      setSelectedId(imported.id);
      setCursor(Math.min(99, imported.bar_count - 1));
      setMessage(`Зафиксировано ${imported.bar_count} закрытых баров`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка импорта");
    } finally {
      setBusy(false);
    }
  };

  const importBinance = async () => {
    setBusy(true);
    setMessage("Загрузка закрытых свечей Binance…");
    try {
      const response = await authorizedFetch(token, "/api/replay/series/import/binance", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          symbol: binanceSymbol.trim().toUpperCase(),
          interval: binanceInterval,
          limit: 500,
          start_time_ms: binanceStart ? new Date(binanceStart).getTime() : null,
          end_time_ms: binanceEnd ? new Date(binanceEnd).getTime() : null,
          paginate: Boolean(binanceStart && binanceEnd),
          max_bars: 100000,
          closed_only: true,
        }),
      });
      if (!response.ok) {
        const error = (await response.json()) as { detail?: string };
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      const imported = (await response.json()) as SeriesSummary;
      await loadSeries();
      setSelectedId(imported.id);
      setCursor(Math.min(99, imported.bar_count - 1));
      setMessage(`Binance: ${imported.bar_count} закрытых баров сохранено`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка импорта Binance");
    } finally {
      setBusy(false);
    }
  };

  const inspectFile = async (file: File) => {
    setBusy(true);
    setMessage(`Анализ формата ${file.name}…`);
    try {
      const response = await authorizedFetch(
        token,
        "/api/replay/series/import/file/inspect",
        {
          method: "POST",
          headers: { "content-type": "application/octet-stream" },
          body: file,
        },
      );
      if (!response.ok) {
        const error = (await response.json()) as { detail?: string };
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      setPendingFile(file);
      setInspection((await response.json()) as OhlcInspection);
      setMessage("Формат определён — проверьте соответствие колонок");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка файла");
    } finally {
      setBusy(false);
    }
  };

  const finishFileImport = async (imported: SeriesSummary) => {
    setPendingFile(null);
    setInspection(null);
    await loadSeries();
    setSelectedId(imported.id);
    setCursor(Math.min(99, imported.bar_count - 1));
    setMessage(
      `${imported.symbol} · ${imported.exchange} · ${imported.bar_count} баров`,
    );
  };

  const addAnnotation = async (
    barIndex: number,
    price: number,
    annotationType: PointType,
  ) => {
    if (!selectedId || barIndex > cursor) return;
    const bar = frame?.bars[barIndex];
    const selectablePrices = bar
      ? manualPriceMode === "high_low"
        ? [Number(bar.high), Number(bar.low)]
        : manualPriceMode === "body"
          ? [Number(bar.open), Number(bar.close)]
          : [Number(bar.close)]
      : [price];
    const snappedPrice = selectablePrices.reduce((nearest, candidate) =>
      Math.abs(candidate - price) < Math.abs(nearest - price) ? candidate : nearest,
    );
    const response = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/annotations`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          bar_index: barIndex,
          point_type: annotationType,
          price: snappedPrice.toString(),
          profile: activeProfile,
          status: "candidate",
        }),
      },
    );
    if (!response.ok) {
      setMessage("Точка не сохранена");
      return;
    }
    const annotation = (await response.json()) as Annotation;
    setAnnotations((current) => [...current, annotation]);
    setActivePointId(annotation.id);
    setMessage(
      `${annotationType.toUpperCase()} сохранена · стало известно ${new Date(annotation.known_at_ms).toLocaleString("ru-RU")}`,
    );
  };

  const moveAnnotation = async (
    annotation: Annotation,
    barIndex: number,
    price: number,
  ) => {
    const bar = frame?.bars[barIndex];
    const selectablePrices = bar
      ? manualPriceMode === "high_low"
        ? [Number(bar.high), Number(bar.low)]
        : manualPriceMode === "body"
          ? [Number(bar.open), Number(bar.close)]
          : [Number(bar.close)]
      : [price];
    const snappedPrice = selectablePrices.reduce((nearest, candidate) =>
      Math.abs(candidate - price) < Math.abs(nearest - price) ? candidate : nearest,
    );
    const response = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/annotations/${annotation.id}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ bar_index: barIndex, price: snappedPrice.toString() }),
      },
    );
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setMessage(payload.detail || "Точка не перемещена");
      return;
    }
    const updated = (await response.json()) as Annotation;
    setAnnotations((current) =>
      current.map((item) => item.id === updated.id ? updated : item),
    );
    const modelResponse = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/models`,
    );
    if (modelResponse.ok) {
      setModels((await modelResponse.json()) as ModelAnnotation[]);
    }
    setMessage(
      `${updated.point_type.toUpperCase()} перемещена на бар ${barIndex + 1}`,
    );
  };

  const removeAnnotation = async (annotation: Annotation) => {
    const response = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/annotations/${annotation.id}`,
      { method: "DELETE" },
    );
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setMessage(payload.detail || "Точка не удалена");
      return;
    }
    setAnnotations((current) =>
      current.filter((item) => item.id !== annotation.id),
    );
    setSelectedPointIds((current) =>
      current.filter((id) => id !== annotation.id),
    );
    setActivePointId((current) => current === annotation.id ? "" : current);
    setMessage(`${annotation.point_type.toUpperCase()} удалена`);
  };

  useEffect(() => {
    if (!activePointId) return;
    const handleDelete = (event: KeyboardEvent) => {
      if (event.key !== "Delete" || event.repeat) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }
      const annotation = annotations.find((item) => item.id === activePointId);
      if (!annotation) return;
      event.preventDefault();
      removeAnnotation(annotation);
    };
    window.addEventListener("keydown", handleDelete);
    return () => window.removeEventListener("keydown", handleDelete);
  }, [activePointId, annotations]);

  const togglePointSelection = (id: string) => {
    setSelectedPointIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : current.length < 6
          ? [...current, id]
          : current,
    );
  };

  const createModelAnnotation = async () => {
    if (!selectedId || selectedPointIds.length < 3) return;
    if (activeProfile === "author_canonical" && !modelSourceRef.trim()) {
      setMessage("Для канона автора укажите первичный источник и таймкод; иначе используйте исследовательский профиль.");
      return;
    }
    const profile = manualPriceMode === "body" ? "research" : activeProfile;
    const response = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/models`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          model_type: modelType,
          direction: modelDirection,
          status: "candidate",
          profile,
          point_ids: selectedPointIds,
          title: modelTitle,
          source_ref: modelSourceRef.trim() || null,
          note: `Ручная разметка · построение: ${constructionPriceModeLabel[manualPriceMode]} · проверка пробоя: ${breakoutPriceModeLabel[breakoutPriceMode]} · p1 задаётся пользователем${manualPriceMode === "body" ? " · режим тел сохраняется только в исследовательском профиле до отдельной сверки с источником" : ""}`,
        }),
      },
    );
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setMessage(payload.detail || "Модель не сохранена");
      return;
    }
    const created = (await response.json()) as ModelAnnotation;
    setModels((current) => [...current, created]);
    setSelectedPointIds([]);
    setModelTitle("");
    setMessage(`${created.model_type} сохранена · версия ${created.version}`);
  };

  const findResearchModels = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams({
        cursor: String(cursor),
        start_bar_index: String(Math.max(0, (frame?.bars.length ?? 1) - 120)),
        window: String(pivotWindow),
        price_mode: researchPriceMode,
        minimum_swing_percent: minimumSwing || "0",
        limit: "20",
      });
      const response = await authorizedFetch(
        token,
        `/api/replay/series/${selectedId}/candidates/mr?${params}`,
      );
      if (!response.ok) {
        const payload = (await response.json()) as { detail?: string };
        throw new Error(payload.detail || `HTTP ${response.status}`);
      }
      const found = (await response.json()) as ResearchMRCandidate[];
      setResearchCandidates(found);
      setSelectedResearchId(found.at(-1)?.id ?? "");
      setMessage(
        found.length
          ? `Найдено ${found.length} исследовательских кандидатов МР`
          : "На видимом префиксе кандидаты не найдены",
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка детектора");
    } finally {
      setBusy(false);
    }
  };

  const compareResearchPriceModes = async () => {
    if (!selectedId || !frame) return;
    setBusy(true);
    try {
      const base = {
        series_id: selectedId,
        end_bar_index: cursor,
        window: pivotWindow,
        minimum_swing_percent: minimumSwing || "0",
      };
      const reports = await Promise.all(
        (["high_low", "close"] as const).map(async (price_mode) => {
          const response = await authorizedFetch(token, "/api/analysis/simulations/detection", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ ...base, price_mode }),
          });
          if (!response.ok) {
            const payload = (await response.json()) as { detail?: string };
            throw new Error(payload.detail || `HTTP ${response.status}`);
          }
          return (await response.json()) as DetectionSimulationReport;
        }),
      );
      const comparison = { high_low: reports[0], close: reports[1] };
      setPriceModeComparison(comparison);
      setMessage(`Сравнение режимов: экстремумы баров — ${comparison.high_low.unique_candidates}, цены закрытия — ${comparison.close.unique_candidates}; сделок 0`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка сравнения режимов");
    } finally {
      setBusy(false);
    }
  };

  const beginCaseFromModel = (model: ModelAnnotation) => {
    const modelPoints = annotations.filter((point) => model.point_ids.includes(point.id));
    const pointBars = modelPoints.map((point) => point.bar_index);
    const start = pointBars.length ? Math.min(...pointBars) : cursor;
    const end = pointBars.length ? Math.max(...pointBars, cursor) : cursor;
    setSelectedModelIds([model.id]);
    setCaseTitle(model.title ? `Разметка: ${model.title}` : `Модель ${model.model_type} ${model.id.slice(0, 8)}`);
    setCaseClassification("unclassified");
    setCaseStart(start);
    setCaseEnd(end);
    setCaseSource(model.source_ref || "Укажите первичный источник и таймкод");
    setCaseTags(`${model.model_type.toLowerCase()}, ${model.profile}, ${model.direction}`);
    setCaseNote(`Факт: вручную сохранена модель ${model.model_type}; p1–p4 и исходный источник требуют проверки. Интерпретация: не задана. Неопределённость: статус ${model.status}; outcome не подставлен.`);
    window.setTimeout(() => document.getElementById("case-builder")?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
    setMessage("Черновик кейса заполнен из модели. Проверьте источник, метаданные и границу знания.");
  };

  const acceptResearchModel = async (candidate: ResearchMRCandidate) => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const createdPoints: Annotation[] = [];
      for (const point of candidate.points) {
        const response = await authorizedFetch(
          token,
          `/api/replay/series/${selectedId}/annotations`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              bar_index: point.bar_index,
              point_type: point.point_type,
              price: point.price,
              profile: "research",
              status: "candidate",
              note: `Опорная точка ${point.pivot_kind === "high" ? "максимума" : "минимума"}; подтверждена на баре ${point.confirmation_bar_index + 1}`,
              source_ref: `${candidate.method}:${candidate.id}`,
              known_at_ms: point.known_at_ms,
            }),
          },
        );
        if (!response.ok) throw new Error("Не удалось сохранить точки кандидата");
        createdPoints.push((await response.json()) as Annotation);
      }
      const modelResponse = await authorizedFetch(
        token,
        `/api/replay/series/${selectedId}/models`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            model_type: "MR",
            direction: candidate.direction,
            status: "candidate",
            profile: "research",
            point_ids: createdPoints.map((point) => point.id),
            title: `Исследовательская модель ${candidate.id.slice(-6)}`,
            note: candidate.trace.join("\n"),
            source_ref: `${candidate.method}:${candidate.id}`,
          }),
        },
      );
      if (!modelResponse.ok) throw new Error("Не удалось сохранить модель-кандидат");
      const createdModel = (await modelResponse.json()) as ModelAnnotation;
      setAnnotations((current) => [...current, ...createdPoints]);
      setModels((current) => [...current, createdModel]);
      setResearchCandidates((current) => current.filter((item) => item.id !== candidate.id));
      setSelectedResearchId("");
      setMessage("Кандидат сохранён в профиле research для экспертной проверки");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка сохранения кандидата");
    } finally {
      setBusy(false);
    }
  };

  const setModelStatus = async (
    model: ModelAnnotation,
    status: ModelAnnotation["status"],
  ) => {
    const response = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/models/${model.id}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ status }),
      },
    );
    if (response.ok) {
      const updated = (await response.json()) as ModelAnnotation;
      setModels((current) =>
        current.map((item) => item.id === updated.id ? updated : item),
      );
    }
  };

  const removeModel = async (model: ModelAnnotation) => {
    const response = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/models/${model.id}`,
      { method: "DELETE" },
    );
    if (response.ok) {
      setModels((current) => current.filter((item) => item.id !== model.id));
    }
  };

  const aggregateTimeframe = async () => {
    if (!selectedId) return;
    setBusy(true);
    const response = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/aggregate`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          target_interval: aggregateInterval,
          timezone: aggregateTimezone,
          session_start: aggregateSession,
          profile: aggregateProfile,
          calendar_id:
            aggregateProfile === "session_observed" ? aggregateCalendar : null,
          calendar_version:
            aggregateProfile === "session_observed"
              ? aggregateCalendarVersion
              : null,
        }),
      },
    );
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setMessage(payload.detail || "Не удалось построить старший таймфрейм");
      setBusy(false);
      return;
    }
    const derived = (await response.json()) as SeriesSummary;
    const lineageResponse = await authorizedFetch(
      token,
      `/api/replay/series/${derived.id}/lineage`,
    );
    const lineage = lineageResponse.ok
      ? ((await lineageResponse.json()) as SeriesLineage)
      : null;
    await loadSeries();
    setSelectedId(derived.id);
    setCursor(Math.min(99, derived.bar_count - 1));
    setBusy(false);
    const quality = lineage?.config.quality;
    const qualityText = quality
      ? ` · отброшено ${quality.dropped_incomplete_buckets} · неполных сохранено ${quality.emitted_incomplete_buckets}`
      : "";
    setMessage(
      `Создан ${derived.interval} из младшего ряда · ${derived.bar_count} баров${qualityText}`,
    );
  };

  const toggleModelSelection = (id: string) => {
    setSelectedModelIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  };

  const createCase = async () => {
    if (!selectedId || !caseTitle.trim()) return;
    const response = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/cases`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          title: caseTitle.trim(),
          classification: caseClassification,
          expected_model_type: modelType,
          profile: activeProfile,
          review_status: "draft",
          start_bar_index: Math.min(caseStart, caseEnd),
          end_bar_index: Math.max(caseStart, caseEnd),
          model_ids: selectedModelIds,
          tags: caseTags.split(",").map((item) => item.trim()).filter(Boolean),
          source_ref: caseSource.trim() || null,
          note: caseNote.trim(),
        }),
      },
    );
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setMessage(payload.detail || "Кейс не сохранён");
      return;
    }
    const created = (await response.json()) as GoldenCase;
    setCases((current) => [created, ...current]);
    setCaseTitle("");
    setCaseNote("");
    setSelectedModelIds([]);
    setMessage(`Кейс «${created.title}» сохранён в профиле ${created.profile}`);
  };

  const prepareAiAnalysis = async () => {
    if (!selectedId || !frame) return;
    setBusy(true);
    try {
      const response = await authorizedFetch(token, "/api/analysis/runs", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          series_id: selectedId, cursor, profile: activeProfile,
          question: analysisQuestion.trim(),
        }),
      });
      if (!response.ok) {
        const payload = (await response.json()) as { detail?: string };
        throw new Error(payload.detail || "Не удалось подготовить AI-пакет");
      }
      const run = (await response.json()) as AnalysisRun;
      setAnalysisRun(run);
      setAnalysisResponses([]);
      setMessage("AI-пакет сохранён: внешний вызов не выполнялся, торговля запрещена");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка AI-пакета");
    } finally {
      setBusy(false);
    }
  };

  const loadAnalysisResponses = async () => {
    if (!analysisRun) return;
    setBusy(true);
    try {
      const response = await authorizedFetch(token, `/api/analysis/runs/${analysisRun.id}/responses`);
      if (!response.ok) throw new Error("Не удалось загрузить сохранённые AI-ответы");
      setAnalysisResponses((await response.json()) as AnalysisResponse[]);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка загрузки AI-ответов");
    } finally {
      setBusy(false);
    }
  };

  const runDetectionSimulation = async () => {
    if (!selectedId || !frame) return;
    setBusy(true);
    try {
      const response = await authorizedFetch(token, "/api/analysis/simulations/detection", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          series_id: selectedId, end_bar_index: cursor, window: pivotWindow,
          price_mode: researchPriceMode, minimum_swing_percent: minimumSwing || "0",
        }),
      });
      if (!response.ok) {
        const payload = (await response.json()) as { detail?: string };
        throw new Error(payload.detail || "Не удалось выполнить аудит");
      }
      const report = (await response.json()) as DetectionSimulationReport;
      setSimulationReport(report);
      setMessage(`Проверено префиксов: ${report.sampled_prefixes}; сделок: 0`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка аудита");
    } finally {
      setBusy(false);
    }
  };

  const runResearchScenario = async () => {
    if (!selectedId || !scenarioModelId || !scenarioHypothesisId) return;
    setBusy(true);
    try {
      const response = await authorizedFetch(token, "/api/analysis/scenarios", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          series_id: selectedId,
          model_id: scenarioModelId,
          hypothesis_id: scenarioHypothesisId,
          end_bar_index: cursor,
          timeout_bars: scenarioTimeout,
          breakout_mode: breakoutPriceMode,
        }),
      });
      if (!response.ok) {
        const payload = (await response.json()) as { detail?: string };
        throw new Error(payload.detail || "Не удалось выполнить сценарий");
      }
      const run = (await response.json()) as ResearchScenarioRun;
      setScenarioRun(run);
      setScenarioBatchResult(null);
      setMessage(`Сценарий сохранён: ${run.events.length} событий, сделок: 0`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка сценария");
    } finally {
      setBusy(false);
    }
  };

  const runResearchScenarioBatch = async () => {
    if (!selectedId || !scenarioHypothesisId) return;
    const modelIds = models
      .filter((item) => item.model_type === "MR" && item.direction !== "unknown" && item.point_ids.length >= 4)
      .map((item) => item.id);
    if (!modelIds.length) {
      setMessage("Нет сохранённых МР с p1–p4 и направлением для пакетного прогона");
      return;
    }
    setBusy(true);
    try {
      const response = await authorizedFetch(token, "/api/analysis/scenarios/batch", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          series_id: selectedId, model_ids: modelIds, hypothesis_id: scenarioHypothesisId,
          end_bar_index: cursor, timeout_bars: scenarioTimeout, breakout_mode: breakoutPriceMode,
          split_fraction: scenarioSplitPercent / 100,
        }),
      });
      if (!response.ok) {
        const payload = (await response.json()) as { detail?: string };
        throw new Error(payload.detail || "Не удалось выполнить пакетный сценарий");
      }
      const result = (await response.json()) as ResearchScenarioBatchResult;
      setScenarioBatchResult(result);
      setScenarioRun(result.runs[0] || null);
      setMessage(`Пакет сохранён: ${result.completed_runs}/${result.requested_models} прогонов, сделок: 0`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка пакетного сценария");
    } finally {
      setBusy(false);
    }
  };

  const runResearchScenarioGlobalBatch = async () => {
    if (!scenarioHypothesisId) return;
    setBusy(true);
    try {
      const response = await authorizedFetch(token, "/api/analysis/scenarios/batch-all", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          hypothesis_id: scenarioHypothesisId, timeout_bars: scenarioTimeout,
          breakout_mode: breakoutPriceMode, split_fraction: scenarioSplitPercent / 100,
        }),
      });
      if (!response.ok) {
        const payload = (await response.json()) as { detail?: string };
        throw new Error(payload.detail || "Не удалось выполнить общий research-прогон");
      }
      const result = (await response.json()) as ResearchScenarioGlobalBatchResult;
      setScenarioGlobalBatchResult(result);
      setMessage(`Общий research-прогон: ${result.completed_runs}/${result.requested_models}; сделок 0`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка общего research-прогона");
    } finally {
      setBusy(false);
    }
  };

  const loadExperimentReport = async () => {
    if (!scenarioHypothesisId) return;
    try {
      const response = await authorizedFetch(token, `/api/analysis/experiments/report?hypothesis_id=${encodeURIComponent(scenarioHypothesisId)}`);
      if (!response.ok) throw new Error("Не удалось сформировать отчёт эксперимента");
      setExperimentReport((await response.json()) as ExperimentReport);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка отчёта эксперимента");
    }
  };

  const setCaseStatus = async (
    goldenCase: GoldenCase,
    reviewStatus: GoldenCase["review_status"],
  ) => {
    const response = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/cases/${goldenCase.id}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ review_status: reviewStatus }),
      },
    );
    if (response.ok) {
      const updated = (await response.json()) as GoldenCase;
      setCases((current) =>
        current.map((item) => item.id === updated.id ? updated : item),
      );
    } else {
      const payload = (await response.json()) as { detail?: string };
      setMessage(payload.detail || "Статус кейса не изменён");
    }
  };

  const removeCase = async (goldenCase: GoldenCase) => {
    const response = await authorizedFetch(
      token,
      `/api/replay/series/${selectedId}/cases/${goldenCase.id}`,
      { method: "DELETE" },
    );
    if (response.ok) {
      setCases((current) => current.filter((item) => item.id !== goldenCase.id));
    }
  };

  const loadCaseMetadata = async (caseId: string) => {
    setMetadataCaseId(caseId);
    const response = await authorizedFetch(token, `/api/replay/series/${selectedId}/cases/${caseId}/metadata`);
    if (!response.ok) return;
    const existing = (await response.json()) as CaseReviewMetadata | null;
    setCaseSourceDraft(cases.find((item) => item.id === caseId)?.source_ref || "");
    setCaseMetadata(existing || { case_id: caseId, video_url: null, timecode_start: null, timecode_end: null, exchange: null, timeframe: null, price_mode: null, visible_until_ms: null, fact: "", interpretation: "", uncertainty: "" });
    const [reviewsResponse, comparisonResponse, gateResponse] = await Promise.all([
      authorizedFetch(token, `/api/replay/series/${selectedId}/cases/${caseId}/reviews`),
      authorizedFetch(token, `/api/replay/series/${selectedId}/cases/${caseId}/comparison`),
      authorizedFetch(token, `/api/replay/series/${selectedId}/cases/${caseId}/gate`),
    ]);
    if (reviewsResponse.ok) setReviews((await reviewsResponse.json()) as CaseReview[]);
    if (comparisonResponse.ok) setComparison((await comparisonResponse.json()) as CaseReviewComparison);
    if (gateResponse.ok) setCaseGate((await gateResponse.json()) as CaseReviewGate);
  };

  const saveCaseMetadata = async () => {
    if (!metadataCaseId) return;
    const response = await authorizedFetch(token, `/api/replay/series/${selectedId}/cases/${metadataCaseId}/metadata`, {
      method: "PUT", headers: { "content-type": "application/json" },
      body: JSON.stringify(caseMetadata),
    });
    if (response.ok) {
      await loadCaseMetadata(metadataCaseId);
      setMessage("Метаданные review сохранены");
    }
  };

  const saveCaseSource = async () => {
    if (!metadataCaseId) return;
    const response = await authorizedFetch(token, `/api/replay/series/${selectedId}/cases/${metadataCaseId}`, {
      method: "PATCH", headers: { "content-type": "application/json" },
      body: JSON.stringify({ source_ref: caseSourceDraft.trim() || null }),
    });
    if (!response.ok) { setMessage("Источник кейса не сохранён"); return; }
    const updated = (await response.json()) as GoldenCase;
    setCases((current) => current.map((item) => item.id === updated.id ? updated : item));
    await loadCaseMetadata(updated.id);
    setMessage("Источник кейса сохранён");
  };

  const copyCaseModelPointsToReview = () => {
    const goldenCase = cases.find((item) => item.id === metadataCaseId);
    if (!goldenCase) return;
    const pointIds = new Set(goldenCase.model_ids.flatMap((modelId) => models.find((model) => model.id === modelId)?.point_ids || []));
    const points = annotations.filter((point) => pointIds.has(point.id)).map((point) => ({
      point_type: point.point_type, bar_index: point.bar_index, price: point.price,
    }));
    setReviewPoints(JSON.stringify(points, null, 2));
  };

  const addIndependentReview = async () => {
    if (!metadataCaseId || !reviewer.trim()) return;
    let points: Record<string, unknown>[];
    try {
      const parsed: unknown = JSON.parse(reviewPoints || "[]");
      if (!Array.isArray(parsed)) throw new Error("points must be array");
      points = parsed as Record<string, unknown>[];
    } catch {
      setMessage("Точки review должны быть JSON-массивом; используйте «Подставить точки модели».");
      return;
    }
    const response = await authorizedFetch(token, `/api/replay/series/${selectedId}/cases/${metadataCaseId}/reviews`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ reviewer: reviewer.trim(), disposition: "independent", classification: reviewClassification, points, fact: reviewFact, interpretation: reviewInterpretation, uncertainty: reviewUncertainty }),
    });
    if (!response.ok) {
      setMessage("Рецензент уже оставил проверку либо данные не приняты");
      return;
    }
    setReviewer(""); setReviewFact(""); setReviewInterpretation(""); setReviewUncertainty(""); setReviewPoints("[]");
    await loadCaseMetadata(metadataCaseId);
    setMessage("Независимый review сохранён");
  };

  const downloadCaseEvidence = async (caseId: string) => {
    const response = await authorizedFetch(token, `/api/replay/series/${selectedId}/cases/${caseId}/evidence`);
    if (!response.ok) { setMessage("Не удалось подготовить пакет доказательств"); return; }
    const payload = await response.json();
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url; link.download = `adversa-case-${caseId}-evidence.json`; link.click();
    URL.revokeObjectURL(url);
  };

  const selected = series.find((item) => item.id === selectedId);

  return (
    <main>
      <header className="topbar">
        <div>
          <span className="brand-mark">A</span>
          <div>
            <strong>Лаборатория Adversa</strong>
            <small>разметка и покадровый просмотр</small>
          </div>
        </div>
        <div className="top-actions">
          <div className="invariants">
            <span>{evidenceTierLabel[activeProfile]}</span>
            <span>время знания не раньше времени события</span>
            <span className="safe">торговля отключена</span>
          </div>
          <span className="current-user">{currentUser.username}</span>
          <div className="menu-wrap">
            <button
              className="menu-button"
              aria-label="Открыть меню"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((value) => !value)}
            >
              <i />
              <i />
              <i />
              <i />
            </button>
            {menuOpen && (
              <div className="app-menu">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenKnowledge();
                  }}
                >
                  База знаний
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenHistory();
                  }}
                >
                  Исторические данные для обучения
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenRules();
                  }}
                >
                  Реестр правил
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenResearchContracts();
                  }}
                >
                  Исследовательские контракты
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenResearchReadiness();
                  }}
                >
                  Готовность контура
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenResearchObservations();
                  }}
                >
                  Журнал наблюдений
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenSimulationSettings();
                  }}
                >
                  Виртуальный эксперимент: параметры
                </button>
                <small>Администрирование</small>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenUsers();
                  }}
                >
                  Пользователи
                </button>
                <button className="logout" onClick={onLogout}>
                  Выйти
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <section className="workspace">
        <aside className="sidebar">
          <div className="panel-heading">
            <span>Набор данных</span>
            <i>{series.length}</i>
          </div>
          <select
            value={selectedId}
            onChange={(event) => {
              const next = series.find(
                (item) => item.id === event.target.value,
              );
              setSelectedId(event.target.value);
              setCursor(Math.min(99, (next?.bar_count ?? 1) - 1));
              setPlaying(false);
            }}
          >
            <option value="">Нет наборов</option>
            {series.map((item) => (
              <option value={item.id} key={item.id}>
                {item.symbol} · {item.interval} · {item.bar_count}
              </option>
            ))}
          </select>
          <div className="bybit-import">
            <input
              value={bybitSymbol}
              aria-label="Инструмент Bybit"
              placeholder="BTCUSDT"
              onChange={(event) => setBybitSymbol(event.target.value)}
            />
            <select
              value={bybitCategory}
              aria-label="Рынок Bybit"
              onChange={(event) => setBybitCategory(event.target.value)}
            >
              <option value="spot">спотовый</option>
              <option value="linear">линейный</option>
              <option value="inverse">обратный</option>
            </select>
            <select
              value={bybitInterval}
              aria-label="Интервал Bybit"
              onChange={(event) => setBybitInterval(event.target.value)}
            >
              {["1", "3", "5", "15", "30", "60", "120", "240", "D", "W"].map(
                (value) => <option key={value} value={value}>{value}</option>,
              )}
            </select>
          </div>
          <button className="primary" onClick={importBybit} disabled={busy}>
            {busy ? "Подождите…" : "Загрузить с Bybit"}
          </button>
          <div className="bybit-import">
            <input
              value={binanceSymbol}
              aria-label="Инструмент Binance"
              placeholder="BTCUSDT"
              onChange={(event) => setBinanceSymbol(event.target.value)}
            />
            <select
              value={binanceInterval}
              aria-label="Интервал Binance"
              onChange={(event) => setBinanceInterval(event.target.value)}
            >
              {["1m", "5m", "15m", "1h", "4h", "1d", "1w", "1M"].map(
                (value) => <option key={value} value={value}>{value}</option>,
              )}
            </select>
            <input type="datetime-local" value={binanceStart} aria-label="Начало периода Binance" onChange={(event) => setBinanceStart(event.target.value)} />
            <input type="datetime-local" value={binanceEnd} aria-label="Конец периода Binance" onChange={(event) => setBinanceEnd(event.target.value)} />
          </div>
          <button className="primary" onClick={importBinance} disabled={busy}>
            {busy ? "Подождите…" : "Загрузить с Binance"}
          </button>
          <label className="file-button">
            Импортировать OHLC-файл
            <input
              type="file"
              accept=".csv,.txt,.tsv,text/csv,text/plain"
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void inspectFile(file);
                event.target.value = "";
              }}
            />
          </label>

          {selected && (
            <dl className="dataset-meta">
              <div>
                <dt>Источник</dt>
                <dd>{selected.source}</dd>
              </div>
              <div>
                <dt>Рынок</dt>
                <dd>{selected.category}</dd>
              </div>
              <div>
                <dt>SHA-256</dt>
                <dd title={selected.sha256}>{selected.sha256.slice(0, 12)}</dd>
              </div>
            </dl>
          )}

          <label className="profile-select">
            Профиль разметки
            <select value={activeProfile} onChange={(event) => setActiveProfile(event.target.value as HistoricalDataset["evidence_tier"])}>
              <option value="author_canonical">канон автора</option>
              <option value="source_anchored">локально подтверждённый</option>
              <option value="experimental">исследовательский</option>
            </select>
          </label>
          <small className="sidebar-note">
            Профиль сохраняется в точках, моделях и кейсах. Он не является разрешением на торговлю.
          </small>

          {selected && (
            <div className="aggregate-box">
              <div className="panel-heading">
                <span>Старший таймфрейм</span>
                <i>из {selected.interval}</i>
              </div>
              <div>
                <select
                  value={aggregateInterval}
                  aria-label="Целевой таймфрейм"
                  onChange={(event) => setAggregateInterval(event.target.value)}
                >
                  {["5", "15", "30", "60", "120", "240", "D", "W"].map(
                    (value) => <option key={value} value={value}>{value}</option>,
                  )}
                </select>
                <select
                  value={aggregateProfile}
                  aria-label="Профиль агрегации"
                  title="Профиль агрегации"
                  onChange={(event) => {
                    const profile = event.target.value as AggregationProfile;
                    setAggregateProfile(profile);
                    if (profile === "session_observed" && aggregateTimezone === "UTC") {
                      setAggregateTimezone("Europe/Moscow");
                    }
                  }}
                >
                  <option value="continuous_strict">Непрерывный строгий</option>
                  <option value="session_observed">Сессионный наблюдаемый</option>
                </select>
                <input
                  value={aggregateTimezone}
                  aria-label="Часовой пояс агрегации"
                  title="Часовой пояс агрегации"
                  onChange={(event) => setAggregateTimezone(event.target.value)}
                />
                <input
                  value={aggregateSession}
                  aria-label="Начало торговой сессии"
                  title="Начало торговой сессии"
                  onChange={(event) => setAggregateSession(event.target.value)}
                />
                {aggregateProfile === "session_observed" && (
                  <>
                    <input
                      value={aggregateCalendar}
                      aria-label="Идентификатор торгового календаря"
                      title="Идентификатор торгового календаря"
                      placeholder="moex_fx"
                      onChange={(event) => setAggregateCalendar(event.target.value)}
                    />
                    <input
                      value={aggregateCalendarVersion}
                      aria-label="Версия торгового календаря"
                      title="Версия торгового календаря"
                      placeholder="2026-06-22"
                      onChange={(event) => setAggregateCalendarVersion(event.target.value)}
                    />
                  </>
                )}
              </div>
              <button
                onClick={aggregateTimeframe}
                disabled={busy}
              >
                Построить старший ТФ
              </button>
            </div>
          )}

          <p className="sidebar-note">
            Нажмите правой кнопкой на графике, чтобы добавить точку. Точка
            получает время знания закрытия выбранного бара.
          </p>
        </aside>

        <section className="lab">
          <div className="lab-heading">
            <div>
              <span>ПОКАДРОВЫЙ ПРОСМОТР / ЭТАП 2</span>
              <h1>
                {selected ? `${selected.symbol} · ${selected.interval}` : "Нет данных"}
              </h1>
            </div>
            <div className="frame-counter">
              <strong>{frame ? frame.cursor + 1 : 0}</strong>
              <span>/ {frame?.total ?? 0} баров</span>
            </div>
          </div>

          <div className="chart-shell">
            {frame && frame.bars.length ? (
              <CandleChart
                frame={frame}
                annotations={annotations.filter(
                  (item) => item.bar_index <= cursor,
                )}
                models={models.filter((item) => item.known_at_ms <= (
                  frame.bars[frame.bars.length - 1]?.known_at_ms ?? 0
                ))}
                researchCandidate={
                  researchCandidates.find((item) => item.id === selectedResearchId) ?? null
                }
                activePointId={activePointId}
                onSelectPoint={setActivePointId}
                onAddPoint={(type, barIndex, price) =>
                  addAnnotation(barIndex, price, type)}
                onMovePoint={moveAnnotation}
                onDeletePoint={removeAnnotation}
              />
            ) : (
              <div className="empty-chart">
                <strong>Нужен неизменяемый OHLCV-набор</strong>
                <span>Загрузите публичные свечи Bybit или локальный CSV.</span>
              </div>
            )}
          </div>

          <div className="transport">
            <button onClick={() => setCursor(0)} disabled={!frame}>|‹</button>
            <button
              onClick={() => setCursor((value) => Math.max(0, value - 10))}
              disabled={!frame}
            >
              −10
            </button>
            <button
              onClick={() => setCursor((value) => Math.max(0, value - 1))}
              disabled={!frame}
            >
              ‹
            </button>
            <button
              className="play"
              onClick={() => setPlaying((value) => !value)}
              disabled={!frame}
            >
              {playing ? "Пауза" : "Пуск"}
            </button>
            <button
              onClick={() =>
                setCursor((value) =>
                  Math.min((frame?.total ?? 1) - 1, value + 1),
                )
              }
              disabled={!frame}
            >
              ›
            </button>
            <button
              onClick={() =>
                setCursor((value) =>
                  Math.min((frame?.total ?? 1) - 1, value + 10),
                )
              }
              disabled={!frame}
            >
              +10
            </button>
            <input
              type="range"
              min={0}
              max={Math.max((frame?.total ?? 1) - 1, 0)}
              value={cursor}
              disabled={!frame}
              onChange={(event) => {
                setCursor(Number(event.target.value));
                setPlaying(false);
              }}
            />
          </div>

          <div className="lower-grid model-grid">
            <section className="annotation-log">
              <div className="panel-heading">
                <span>Разметка</span>
                <i>{annotations.length}</i>
              </div>
              {annotations.length ? (
                annotations
                  .slice()
                  .reverse()
                  .map((annotation) => (
                    <div
                      key={annotation.id}
                      className={
                        selectedPointIds.includes(annotation.id)
                          ? "annotation-row selected"
                          : activePointId === annotation.id
                            ? "annotation-row active"
                            : "annotation-row"
                      }
                    >
                      <button
                        title="Выбрать точку; повторный клик добавляет или убирает её из модели"
                        onClick={() => {
                          setActivePointId(annotation.id);
                          togglePointSelection(annotation.id);
                        }}
                      >
                        <b>{annotation.point_type.toUpperCase()}</b>
                        <span>бар {annotation.bar_index + 1}</span>
                        <span>{formatPrice(Number(annotation.price))}</span>
                        <i>{annotation.status}</i>
                      </button>
                    </div>
                  ))
              ) : (
                <p>Кликните по графику, чтобы добавить выбранную точку.</p>
              )}
            </section>
            <section className="model-panel">
              <div className="panel-heading">
                <span>Модели</span>
                <i>{models.length}</i>
              </div>
              <div className="model-builder">
                <input
                  value={modelTitle}
                  placeholder="Название / номер примера"
                  onChange={(event) => setModelTitle(event.target.value)}
                />
                <input
                  value={modelSourceRef}
                  placeholder="Источник / урок / таймкод (обязателен для канона)"
                  onChange={(event) => setModelSourceRef(event.target.value)}
                />
                <select
                  value={modelType}
                  onChange={(event) =>
                    setModelType(event.target.value as ModelAnnotation["model_type"])
                  }
                >
                  <option value="MR">МР</option>
                  <option value="MP">МП</option>
                  <option value="MDR">МДР</option>
                  <option value="unknown">не определена</option>
                </select>
                <select
                  value={modelDirection}
                  onChange={(event) =>
                    setModelDirection(
                      event.target.value as ModelAnnotation["direction"],
                    )
                  }
                >
                  <option value="unknown">направление?</option>
                  <option value="up">вверх</option>
                  <option value="down">вниз</option>
                </select>
                <select
                  value={manualPriceMode}
                  title="Новые и перемещаемые точки привязываются к ближайшему допустимому значению выбранного режима"
                  onChange={(event) =>
                    setManualPriceMode(event.target.value as ConstructionPriceMode)
                  }
                >
                  <option value="high_low">экстремумы баров</option>
                  <option value="body">границы тел баров (research)</option>
                  <option value="close">цены закрытия</option>
                </select>
                <select
                  value={breakoutPriceMode}
                  title="Правило, по которому в этой ручной разметке проверяется прохождение уровня"
                  onChange={(event) =>
                    setBreakoutPriceMode(event.target.value as BreakoutPriceMode)
                  }
                >
                  <option value="high_low">пробой: high/low</option>
                  <option value="close">пробой: закрытие</option>
                </select>
                <button
                  className="primary"
                  disabled={selectedPointIds.length < 3}
                  onClick={createModelAnnotation}
                >
                  Создать из точек ({selectedPointIds.length}/3–6)
                </button>
              </div>
              <small className="manual-mr-hint">
                Ручная МР: сначала выберите режим цены и направление, затем
                поставьте p1 в подтверждённой исходной точке. Точки при добавлении
                и переносе фиксируются на ближайшем экстремуме, границе тела либо цене закрытия. Режим построения и правило пробоя сохраняются раздельно в модели.
                Это разметка-кандидат, а не автоматическое распознавание.
                Режим «границы тел» всегда сохраняется в исследовательском профиле и не повышает статус канона.
                Для канона автора укажите источник и таймкод; полная каноническая база всё равно требует p1–p4 и проверку источника.
              </small>
              <div className="research-detector">
                <strong>Исследовательский поиск кандидатов МР</strong>
                <div>
                  <label>Окно pivot
                    <input type="number" min={1} max={30} value={pivotWindow} onChange={(event) => setPivotWindow(Math.max(1, Math.min(30, Number(event.target.value) || 1)))} />
                  </label>
                  <label>Мин. swing, %
                    <input value={minimumSwing} onChange={(event) => setMinimumSwing(event.target.value)} />
                  </label>
                  <label>Цена
                    <select value={researchPriceMode} onChange={(event) => setResearchPriceMode(event.target.value as "high_low" | "close")}>
                      <option value="high_low">экстремумы баров</option>
                      <option value="close">цены закрытия</option>
                    </select>
                  </label>
                  <button onClick={() => void findResearchModels()} disabled={busy || !frame}>Найти кандидаты</button>
                  <button onClick={() => void compareResearchPriceModes()} disabled={busy || !frame}>Сравнить режимы цен</button>
                </div>
                <small>
                  Только исследовательский профиль: это эвристика, а не авторский МР и не
                  торговый сигнал. Край видимого окна не назначает p1.
                </small>
                {researchCandidates.map((candidate) => (
                  <div className={selectedResearchId === candidate.id ? "research-row selected" : "research-row"} key={candidate.id}>
                    <button onClick={() => setSelectedResearchId(candidate.id)}>
                      <b>{candidate.direction === "up" ? "АП" : "ДАУН"}</b>
                      <span>{candidate.points.map((point) => `${point.point_type}@${point.bar_index + 1}`).join(" · ")}</span>
                      <small>{constructionPriceModeLabel[candidate.price_mode]} · оценка {candidate.score.toFixed(2)}</small>
                    </button>
                    <button onClick={() => void acceptResearchModel(candidate)}>Сохранить</button>
                  </div>
                ))}
                {priceModeComparison && (
                  <small>
                    Сравнение одного префикса: экстремумы баров — {priceModeComparison.high_low.sampled_prefixes} префиксов,
                    {" "}{priceModeComparison.high_low.unique_candidates} кандидатов; цены закрытия — {priceModeComparison.close.sampled_prefixes} префиксов,
                    {" "}{priceModeComparison.close.unique_candidates} кандидатов. Это аудит различий, не торговый сигнал.
                  </small>
                )}
              </div>
              <div className="research-detector scenario-lab">
                <strong>Лаборатория тестовых гипотез</strong>
                <div>
                  <label>Модель
                    <select value={scenarioModelId} onChange={(event) => setScenarioModelId(event.target.value)}>
                      <option value="">выберите МР с p1–p4</option>
                      {models.filter((item) => item.model_type === "MR" && item.direction !== "unknown" && item.point_ids.length >= 4).map((item) => (
                        <option value={item.id} key={item.id}>{item.title || item.id.slice(0, 8)} · {directionLabel[item.direction]}</option>
                      ))}
                    </select>
                  </label>
                  <label>Гипотеза
                    <select value={scenarioHypothesisId} onChange={(event) => setScenarioHypothesisId(event.target.value)}>
                      {researchHypotheses.map((item) => <option value={item.id} key={item.id}>{item.status === "blocked" ? "⛔ " : ""}{item.title}</option>)}
                    </select>
                  </label>
                  <label>Лимит баров
                    <input type="number" min={1} max={10000} value={scenarioTimeout} onChange={(event) => setScenarioTimeout(Math.max(1, Number(event.target.value) || 1))} />
                  </label>
                  <label>Обучающая часть, %
                    <input type="number" min={50} max={90} value={scenarioSplitPercent} onChange={(event) => setScenarioSplitPercent(Math.max(50, Math.min(90, Number(event.target.value) || 70)))} />
                  </label>
                  <button onClick={() => void runResearchScenario()} disabled={busy || !scenarioModelId || !scenarioHypothesisId}>Запустить без сделок</button>
                  <button onClick={() => void runResearchScenarioBatch()} disabled={busy || !scenarioHypothesisId}>Запустить по всем готовым МР</button>
                  <button onClick={() => void runResearchScenarioGlobalBatch()} disabled={busy || !scenarioHypothesisId}>Прогнать все ряды</button>
                  <button onClick={() => void loadExperimentReport()} disabled={!scenarioHypothesisId}>Отчёт эксперимента</button>
                </div>
                {researchHypotheses.find((item) => item.id === scenarioHypothesisId) && (() => {
                  const hypothesis = researchHypotheses.find((item) => item.id === scenarioHypothesisId)!;
                  return <small>Источник: {hypothesis.source_refs.join(" · ")} · {labelResearchStatus(hypothesis.status)}{hypothesis.block_reason ? ` · ${hypothesis.block_reason}` : ""}</small>;
                })()}
                {modelTrace && (
                  <div className="scenario-trace">
                    <b>Трасса модели · {labelResearchStatus(modelTrace.current_state)} · сделки запрещены</b>
                    {modelTrace.trace.map((item, index) => (
                      <small key={`${item.state}-${index}`}>
                        {item.label} · {item.reason}{item.bar_index !== null ? ` · бар ${item.bar_index + 1}` : ""}
                      </small>
                    ))}
                  </div>
                )}
                {scenarioRun && (
                  <div className="scenario-journal">
                    <b>Журнал {scenarioRun.hypothesis_id}: {labelResearchStatus(scenarioRun.status)}</b>
                    {scenarioRun.config.outcome && <small>Наблюдаемый исход: {labelResearchEvent(scenarioRun.config.outcome.kind)}{scenarioRun.config.outcome.bar_index !== null ? ` · бар ${scenarioRun.config.outcome.bar_index + 1}` : ""}. Не является сделкой.</small>}
                    {scenarioRun.levels.map((level) => <small key={level.id}>{level.id}: {level.price} · {level.note}</small>)}
                    {scenarioRun.events.map((event, index) => <small key={`${event.kind}-${index}`}>{labelResearchEvent(event.kind)} · бар {event.bar_index + 1} · стало известно {new Date(event.known_at_ms).toLocaleString("ru-RU")}</small>)}
                  </div>
                )}
                {experimentReport && (
                  <div className="scenario-journal">
                    <b>Воспроизводимый отчёт: {experimentReport.hypothesis_id} · прогонов {experimentReport.run_count}</b>
                    <small>{experimentReport.notice}</small>
                    <small>Порядок: по времени знания модели; сделки запрещены; издержки не применяются без отдельного торгового контракта.</small>
                    {Object.entries(experimentReport.evaluation.groups).map(([group, value]) => <small key={group}>{group === "all" ? "весь корпус" : group === "in_sample" ? "обучающая часть" : "проверочная часть"}: {value.sample_count} · {Object.entries(value.outcomes).map(([kind, count]) => `${labelResearchEvent(kind)}: ${count}`).join(", ") || "нет исходов"}</small>)}
                    {experimentReport.rows.slice(0, 10).map((row) => <small key={row.run_id}>{row.symbol} · {row.interval} · {row.provider}: {labelResearchEvent(row.outcome.kind)}; сделки запрещены</small>)}
                  </div>
                )}
                {scenarioBatchResult && (
                  <div className="scenario-journal">
                    <b>Пакетный журнал {scenarioBatchResult.hypothesis_id}: {scenarioBatchResult.completed_runs}/{scenarioBatchResult.requested_models}</b>
                    {Object.entries(scenarioBatchResult.summary).map(([key, value]) => <small key={key}>{key.startsWith("run_status:") ? `статус: ${labelResearchStatus(key.slice(11))}` : labelResearchEvent(key)}: {value}</small>)}
                    <b>Последовательная проверка по времени · {Math.round(scenarioBatchResult.evaluation.split_fraction * 100)}% / {100 - Math.round(scenarioBatchResult.evaluation.split_fraction * 100)}% · сделки запрещены</b>
                    {Object.entries(scenarioBatchResult.evaluation.groups).map(([group, value]) => (
                      <small key={group}>{group === "all" ? "весь корпус" : group === "in_sample" ? "обучающая часть" : "проверочная часть"}: {value.sample_count} моделей · {Object.entries(value.outcomes).map(([kind, count]) => `${labelResearchEvent(kind)}: ${count}`).join(", ") || "нет исходов"}</small>
                    ))}
                    <small>Издержки: {scenarioBatchResult.evaluation.cost_model}</small>
                    {scenarioBatchResult.evaluation.limitations.map((item) => <small key={item}>{item}</small>)}
                    {scenarioBatchResult.skipped_models.map((item) => <small key={item.model_id}>Пропущена {item.model_id.slice(0, 8)}: {item.reason}</small>)}
                  </div>
                )}
                {scenarioGlobalBatchResult && (
                  <div className="scenario-journal">
                    <b>Общее исследование: {scenarioGlobalBatchResult.completed_runs}/{scenarioGlobalBatchResult.requested_models} · сделки запрещены</b>
                    {Object.entries(scenarioGlobalBatchResult.summary).map(([key, value]) => <small key={key}>{key.startsWith("run_status:") ? `статус: ${labelResearchStatus(key.slice(11))}` : labelResearchEvent(key)}: {value}</small>)}
                    <b>Серии</b>
                    {Object.entries(scenarioGlobalBatchResult.by_series).map(([seriesId, value]) => <small key={seriesId}>{seriesId.slice(0, 8)} · прогонов {value.runs} · {Object.entries(value.outcomes).map(([key, count]) => `${labelResearchEvent(key)}: ${count}`).join(", ")}</small>)}
                    <small>Последовательная проверка по времени; сделки запрещены.</small>
                  </div>
                )}
              </div>
              {models.map((model) => (
                <div
                  className={
                    selectedModelIds.includes(model.id)
                      ? "model-row selected"
                      : "model-row"
                  }
                  key={model.id}
                >
                  <button
                    className="select-model"
                    title="Включить модель в эталонный кейс"
                    onClick={() => toggleModelSelection(model.id)}
                  >
                    {selectedModelIds.includes(model.id) ? "✓" : "○"}
                  </button>
                  <div>
                    <b>{model.model_type}</b>
                    <span>{model.title || model.direction}</span>
                    <small>v{model.version} · {model.point_ids.length} точек</small>
                    <small>{model.source_ref || "источник модели не задан"}</small>
                  </div>
                  <select
                    value={model.status}
                    onChange={(event) =>
                      setModelStatus(
                        model,
                        event.target.value as ModelAnnotation["status"],
                      )
                    }
                  >
                  <option value="candidate">кандидат</option>
                  <option value="confirmed">подтверждена</option>
                  <option value="invalidated">опровергнута</option>
                  <option value="expired">утратила актуальность</option>
                  </select>
                  <button title="Заполнить черновик кейса этой моделью" onClick={() => beginCaseFromModel(model)}>В кейс</button>
                  <button onClick={() => removeModel(model)}>×</button>
                </div>
              ))}
              {!models.length && (
                <p>Выберите 3–6 точек в журнале разметки и создайте модель.</p>
              )}
            </section>
            <section className="trace-panel">
              <div className="panel-heading">
                <span>Журнал проверки</span>
                <i>детерминированный</i>
              </div>
              <p>{message}</p>
              <code>
                позиция={frame?.cursor ?? "—"} · будущие бары скрыты ·
                профиль={evidenceTierLabel[activeProfile]}
              </code>
              <div className="analysis-tools">
                <strong>ИИ-анализ / аудит префикса</strong>
                <input
                  value={analysisQuestion}
                  placeholder="Вопрос к будущему ИИ-аналитику"
                  onChange={(event) => setAnalysisQuestion(event.target.value)}
                />
                <div>
                  <button onClick={() => void prepareAiAnalysis()} disabled={busy || !frame}>
                    Подготовить пакет для ИИ
                  </button>
                  <button onClick={() => void runDetectionSimulation()} disabled={busy || !frame}>
                    Проверить префиксы
                  </button>
                </div>
                {analysisRun && (
                  <div>
                    <small>
                      Пакет {analysisRun.packet_sha256.slice(0, 12)} · {analysisRun.status} · торговля: {analysisRun.response.trade_permission || "запрещена"}
                    </small>
                    <button onClick={() => void loadAnalysisResponses()} disabled={busy}>Показать сохранённые ответы</button>
                    {analysisResponses.map((item) => (
                      <small key={item.id}>
                        {item.provider}{item.model_name ? ` / ${item.model_name}` : ""} · уверенность: {item.response.confidence} · фактов: {item.response.facts.length} · неизвестностей: {item.response.unknowns.length}
                      </small>
                    ))}
                  </div>
                )}
                {simulationReport && (
                  <small>
                    Префиксов {simulationReport.sampled_prefixes}; новых кандидатов {simulationReport.unique_candidates}; сделок {simulationReport.trade_count}.
                  </small>
                )}
              </div>
            </section>
            <section className="case-panel">
              <div className="panel-heading">
                <span>Разметка кейсов</span>
                <i>{cases.length}</i>
              </div>
              <details className="new-case-panel">
                <summary>Создать новый черновик кейса</summary>
              <div className="case-builder" id="case-builder">
                <input
                  value={caseTitle}
                  placeholder="Название примера"
                  onChange={(event) => setCaseTitle(event.target.value)}
                />
                <select
                  value={caseClassification}
                  onChange={(event) =>
                    setCaseClassification(
                      event.target.value as GoldenCase["classification"],
                    )
                  }
                >
                  <option value="unclassified">без класса до проверки</option>
                  <option value="positive">положительный</option>
                  <option value="negative">отрицательный</option>
                  <option value="boundary">пограничный</option>
                </select>
                <input
                  value={caseSource}
                  placeholder="Источник: урок и таймкод"
                  onChange={(event) => setCaseSource(event.target.value)}
                />
                <input
                  value={caseTags}
                  placeholder="Теги через запятую"
                  onChange={(event) => setCaseTags(event.target.value)}
                />
                <textarea
                  value={caseNote}
                  placeholder="Факт, интерпретация, неопределённость; outcome не подставляйте заранее"
                  onChange={(event) => setCaseNote(event.target.value)}
                  rows={3}
                />
                <div className="case-range">
                  <button onClick={() => setCaseStart(cursor)}>
                    Начало = бар {caseStart + 1}
                  </button>
                  <button onClick={() => setCaseEnd(cursor)}>
                    Конец = бар {caseEnd + 1}
                  </button>
                  <span>моделей: {selectedModelIds.length}</span>
                </div>
                <button
                  className="primary"
                  disabled={!caseTitle.trim()}
                  onClick={createCase}
                >
                  Сохранить черновик кейса
                </button>
              </div>
              </details>
              <div className="case-list">
                {cases.map((goldenCase) => (
                  <div className={`case-row case-${goldenCase.classification}`} key={goldenCase.id}>
                    <div>
                      <b>{goldenCase.title}</b>
                      <span>
                        бары {goldenCase.start_bar_index + 1}–
                        {goldenCase.end_bar_index + 1} · {goldenCase.expected_model_type}
                      </span>
                      <small>
                        {caseClassificationLabel[goldenCase.classification]} · {goldenCase.source_ref || "без источника"}
                      </small>
                      {goldenCase.note && <small>{goldenCase.note}</small>}
                    </div>
                    <select
                      value={goldenCase.review_status}
                      onChange={(event) =>
                        setCaseStatus(
                          goldenCase,
                          event.target.value as GoldenCase["review_status"],
                        )
                      }
                    >
                      <option value="draft">{caseStatusLabel.draft}</option>
                      <option value="reviewed">{caseStatusLabel.reviewed}</option>
                      <option value="approved">{caseStatusLabel.approved}</option>
                    </select>
                    <div className="case-actions">
                      <button onClick={() => void loadCaseMetadata(goldenCase.id)}>Открыть карточку</button>
                      <button onClick={() => void downloadCaseEvidence(goldenCase.id)}>Скачать пакет</button>
                      <button onClick={() => { setBlindCaseId(goldenCase.id); setMessage("Слепой просмотр: будущие закрытые бары скрыты"); }}>Слепой просмотр</button>
                      <button className="danger" onClick={() => removeCase(goldenCase)}>Удалить</button>
                    </div>
                  </div>
                ))}
                {!cases.length && <p>Эталонные примеры этого ряда ещё не созданы.</p>}
              </div>
              {metadataCaseId && (
                <div className="case-builder">
                  <small>Данные проверки: факт, интерпретация и неопределённость хранятся отдельно.</small>
                  {caseGate && (
                    <div className={`case-gate ${caseGate.review_ready ? "ready" : "pending"}`}>
                      <b>{caseGate.review_ready ? "Пакет готов к утверждению после проверки" : "Не хватает доказательств для утверждения"}</b>
                      <small>Состояние жизненного цикла: {labelResearchStatus(caseGate.lifecycle_state)} · {caseGate.lifecycle_reason}</small>
                      {!caseGate.review_ready && <ul>{caseGate.missing_requirements.map((item) => <li key={item}>{item}</li>)}</ul>}
                      <small>{caseGate.note}</small>
                    </div>
                  )}
                  <small>Привязка visual-only: укажите первичный источник, затем инструмент/биржу, ТФ, режим цены и границу знания. Это не создаёт точки автоматически.</small>
                  <input value={caseSourceDraft} placeholder="Первичный источник / урок / таймкод" onChange={(event) => setCaseSourceDraft(event.target.value)} />
                  <button onClick={() => void saveCaseSource()}>Сохранить источник кейса</button>
                  <input value={caseMetadata.video_url || ""} placeholder="URL видео" onChange={(event) => setCaseMetadata({ ...caseMetadata, video_url: event.target.value || null })} />
                  <input value={caseMetadata.timecode_start || ""} placeholder="Таймкод с" onChange={(event) => setCaseMetadata({ ...caseMetadata, timecode_start: event.target.value || null })} />
                  <input value={caseMetadata.timecode_end || ""} placeholder="Таймкод по" onChange={(event) => setCaseMetadata({ ...caseMetadata, timecode_end: event.target.value || null })} />
                  <input value={caseMetadata.exchange || ""} placeholder="Биржа / источник графика" onChange={(event) => setCaseMetadata({ ...caseMetadata, exchange: event.target.value || null })} />
                  <input value={caseMetadata.timeframe || ""} placeholder="Таймфрейм" onChange={(event) => setCaseMetadata({ ...caseMetadata, timeframe: event.target.value || null })} />
                  <select value={caseMetadata.price_mode || ""} onChange={(event) => setCaseMetadata({ ...caseMetadata, price_mode: event.target.value || null })}><option value="">Режим построения?</option><option value="high_low">экстремумы баров</option><option value="body">тела баров</option><option value="close">цены закрытия</option></select>
                  <label>Граница знания <input type="datetime-local" value={caseMetadata.visible_until_ms ? new Date(caseMetadata.visible_until_ms).toISOString().slice(0, 16) : ""} onChange={(event) => setCaseMetadata({ ...caseMetadata, visible_until_ms: event.target.value ? new Date(event.target.value).getTime() : null })} /></label>
                  <textarea value={caseMetadata.fact} placeholder="Факт источника" onChange={(event) => setCaseMetadata({ ...caseMetadata, fact: event.target.value })} />
                  <textarea value={caseMetadata.interpretation} placeholder="Интерпретация" onChange={(event) => setCaseMetadata({ ...caseMetadata, interpretation: event.target.value })} />
                  <textarea value={caseMetadata.uncertainty} placeholder="Неопределённость / проверить" onChange={(event) => setCaseMetadata({ ...caseMetadata, uncertainty: event.target.value })} />
                  <button onClick={() => void saveCaseMetadata()}>Сохранить метаданные проверки</button>
                  <hr />
                  <small>Независимая проверка не меняет карточку кейса и не должна видеть будущие бары.</small>
                  <input value={reviewer} placeholder="Рецензент" onChange={(event) => setReviewer(event.target.value)} />
                  <select value={reviewClassification} onChange={(event) => setReviewClassification(event.target.value as GoldenCase["classification"])}><option value="unclassified">без класса</option><option value="positive">положительный</option><option value="negative">отрицательный</option><option value="boundary">пограничный</option></select>
                  <textarea value={reviewFact} placeholder="Факт проверки" onChange={(event) => setReviewFact(event.target.value)} />
                  <textarea value={reviewInterpretation} placeholder="Интерпретация проверки" onChange={(event) => setReviewInterpretation(event.target.value)} />
                  <textarea value={reviewUncertainty} placeholder="Неопределённость проверки" onChange={(event) => setReviewUncertainty(event.target.value)} />
                  <button onClick={copyCaseModelPointsToReview}>Подставить точки модели в review</button>
                  <textarea value={reviewPoints} placeholder='Точки review JSON, например [{"point_type":"p1","bar_index":12,"price":"1.234"}]' onChange={(event) => setReviewPoints(event.target.value)} />
                  <button disabled={!reviewer.trim()} onClick={() => void addIndependentReview()}>Сохранить независимую проверку</button>
                  <small>Проверок: {comparison?.review_count ?? 0}; независимых рецензентов: {comparison?.unique_reviewer_count ?? 0}; согласованность: {comparison?.consensus ? reviewConsensusLabel[comparison.consensus] || comparison.consensus : "—"}</small>
                  <small>Согласованность точек: {comparison?.point_consensus ? reviewConsensusLabel[comparison.point_consensus] || comparison.point_consensus : "—"}</small>
                  {comparison && Object.entries(comparison.point_signatures).map(([role, signatures]) => <small key={role}>{role}: {signatures.join(" | ")}</small>)}
                  {reviews.map((review) => <small key={review.id}>{review.reviewer}: {caseClassificationLabel[review.classification]} · {reviewDispositionLabel[review.disposition] || review.disposition}</small>)}
                </div>
              )}
              {blindCaseId && <button onClick={() => setBlindCaseId("")}>Выйти из слепого просмотра</button>}
            </section>
          </div>
        </section>
      </section>
      {pendingFile && inspection && (
        <OhlcImportDialog
          token={token}
          file={pendingFile}
          inspection={inspection}
          onClose={() => {
            setPendingFile(null);
            setInspection(null);
          }}
          onImported={(imported) => void finishFileImport(imported)}
        />
      )}
    </main>
  );
}

function KnowledgeBase({
  token,
  onClose,
}: {
  token: string;
  onClose: () => void;
}) {
  const [path, setPath] = useState("");
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [selected, setSelected] = useState<KnowledgePreview | null>(null);
  const [assetUrl, setAssetUrl] = useState("");
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  const releaseAsset = useCallback(() => {
    setAssetUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return "";
    });
  }, []);

  const loadDirectory = useCallback(async (nextPath: string) => {
    setError("");
    releaseAsset();
    setSelected(null);
    const response = await authorizedFetch(
      token,
      `/api/knowledge/tree?path=${encodeURIComponent(nextPath)}`,
    );
    if (!response.ok) throw new Error("Не удалось открыть каталог");
    const payload = (await response.json()) as {
      path: string;
      entries: KnowledgeEntry[];
    };
    setPath(payload.path);
    setEntries(payload.entries);
    setSearching(false);
  }, [releaseAsset, token]);

  useEffect(() => {
    loadDirectory("").catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : "Ошибка"),
    );
    return releaseAsset;
  }, [loadDirectory, releaseAsset]);

  const openFile = async (entry: KnowledgeEntry) => {
    setError("");
    releaseAsset();
    const response = await authorizedFetch(
      token,
      `/api/knowledge/preview?path=${encodeURIComponent(entry.path)}`,
    );
    if (!response.ok) {
      setError("Не удалось открыть файл");
      return;
    }
    setSelected((await response.json()) as KnowledgePreview);
    if (entry.preview_kind === "image" || entry.preview_kind === "pdf") {
      const asset = await authorizedFetch(
        token,
        `/api/knowledge/download?path=${encodeURIComponent(entry.path)}`,
      );
      if (asset.ok) setAssetUrl(URL.createObjectURL(await asset.blob()));
    }
  };

  const download = async (url: string, filename: string) => {
    const response = await authorizedFetch(token, url);
    if (!response.ok) {
      setError("Не удалось скачать файл");
      return;
    }
    const objectUrl = URL.createObjectURL(await response.blob());
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const search = async () => {
    if (query.trim().length < 2) return;
    setError("");
    const response = await authorizedFetch(
      token,
      `/api/knowledge/search?query=${encodeURIComponent(query.trim())}`,
    );
    if (!response.ok) {
      setError("Не удалось выполнить поиск");
      return;
    }
    const payload = (await response.json()) as { results: KnowledgeEntry[] };
    setEntries(payload.results);
    setSearching(true);
  };

  const breadcrumbs = path ? path.split("/") : [];
  return (
    <section className="knowledge-overlay">
      <div className="knowledge-page">
        <header>
          <div>
            <small>Материалы проекта</small>
            <h1>База знаний</h1>
          </div>
          <div className="knowledge-actions">
            <button
              onClick={() => download("/api/knowledge/archive", "adversa-knowledge.zip")}
            >
              Скачать всё
            </button>
            <button onClick={onClose}>Закрыть</button>
          </div>
        </header>
        <div className="knowledge-search">
          <input
            value={query}
            placeholder="Поиск по названию и тексту"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") search();
            }}
          />
          <button onClick={search}>Найти</button>
        </div>
        <nav className="knowledge-breadcrumbs">
          <button onClick={() => loadDirectory("")}>Корень</button>
          {breadcrumbs.map((part, index) => (
            <span key={`${part}-${index}`}>
              /
              <button
                onClick={() => loadDirectory(breadcrumbs.slice(0, index + 1).join("/"))}
              >
                {part}
              </button>
            </span>
          ))}
          {searching && <span>/ результаты поиска</span>}
        </nav>
        {error && <p className="knowledge-error">{error}</p>}
        <div className="knowledge-workspace">
          <aside className="knowledge-list">
            {!searching && path && (
              <button
                className="knowledge-entry directory"
                onClick={() => loadDirectory(path.split("/").slice(0, -1).join("/"))}
              >
                <span>↑</span><strong>На уровень выше</strong>
              </button>
            )}
            {entries.map((entry) => (
              <button
                className={`knowledge-entry ${entry.kind}`}
                key={entry.path}
                onClick={() =>
                  entry.kind === "directory"
                    ? loadDirectory(entry.path)
                    : openFile(entry)
                }
              >
                <span>{entry.kind === "directory" ? "▰" : "◇"}</span>
                <strong>{entry.name}</strong>
                {entry.size !== null && <small>{formatBytes(entry.size)}</small>}
              </button>
            ))}
            {!entries.length && <p className="knowledge-empty">Ничего не найдено</p>}
          </aside>
          <article className="knowledge-viewer">
            {!selected && <p>Выберите файл для просмотра.</p>}
            {selected && (
              <>
                <div className="knowledge-file-head">
                  <div>
                    <h2>{selected.entry.name}</h2>
                    <small>{selected.entry.path}</small>
                  </div>
                  <button
                    onClick={() =>
                      download(
                        `/api/knowledge/download?path=${encodeURIComponent(selected.entry.path)}`,
                        selected.entry.name,
                      )
                    }
                  >
                    Скачать
                  </button>
                </div>
                {selected.entry.preview_kind === "text" && (
                  <pre>{selected.content}</pre>
                )}
                {selected.entry.preview_kind === "image" && assetUrl && (
                  <img src={assetUrl} alt={selected.entry.name} />
                )}
                {selected.entry.preview_kind === "pdf" && assetUrl && (
                  <iframe src={assetUrl} title={selected.entry.name} />
                )}
                {selected.entry.preview_kind === "binary" && (
                  <p>Предпросмотр недоступен. Скачайте файл, чтобы открыть его локально.</p>
                )}
              </>
            )}
          </article>
        </div>
      </div>
    </section>
  );
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} Б`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} КБ`;
  return `${(value / 1024 / 1024).toFixed(1)} МБ`;
}

function RuleRegistry({
  token,
  onClose,
}: {
  token: string;
  onClose: () => void;
}) {
  const [rules, setRules] = useState<RuleDefinition[]>([]);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const [draft, setDraft] = useState({
    id: "",
    scope: "mr.",
    title: "",
    statement: "",
    sources: "",
  });

  const loadRules = useCallback(async () => {
    const response = await authorizedFetch(token, "/api/rules");
    if (!response.ok) throw new Error("Не удалось загрузить правила");
    setRules((await response.json()) as RuleDefinition[]);
  }, [token]);

  useEffect(() => {
    loadRules().catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : "Ошибка"),
    );
  }, [loadRules]);

  const create = async () => {
    setError("");
    const response = await authorizedFetch(token, "/api/rules", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        id: draft.id.trim().toUpperCase(),
        profile: "author_canonical",
        scope: draft.scope.trim(),
        title: draft.title.trim(),
        statement: draft.statement.trim(),
        status: "unknown",
        confidence: "unknown",
        sources: draft.sources.split(",").map((item) => item.trim()).filter(Boolean),
      }),
    });
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Правило не сохранено");
      return;
    }
    setDraft({ id: "", scope: "mr.", title: "", statement: "", sources: "" });
    await loadRules();
  };

  const setStatus = async (
    rule: RuleDefinition,
    status: RuleDefinition["status"],
  ) => {
    const response = await authorizedFetch(token, `/api/rules/${rule.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (response.ok) {
      const updated = (await response.json()) as RuleDefinition;
      setRules((current) =>
        current.map((item) => item.id === updated.id ? updated : item),
      );
    }
  };

  const shown = rules.filter((rule) => {
    const needle = filter.trim().toLocaleLowerCase("ru");
    return !needle || `${rule.id} ${rule.scope} ${rule.title} ${rule.statement}`
      .toLocaleLowerCase("ru")
      .includes(needle);
  });

  return (
    <section className="knowledge-overlay">
      <div className="rules-page">
        <header>
          <div>
            <small>Этап 3 · канон автора</small>
            <h1>Реестр правил МР</h1>
          </div>
          <button onClick={onClose}>Закрыть</button>
        </header>
        <div className="rule-toolbar">
          <input
            value={filter}
            placeholder="Фильтр правил"
            onChange={(event) => setFilter(event.target.value)}
          />
          <span>{shown.length} / {rules.length}</span>
        </div>
        <div className="rule-create">
          <input
            value={draft.id}
            placeholder="Идентификатор правила"
            onChange={(event) => setDraft({ ...draft, id: event.target.value })}
          />
          <input
            value={draft.scope}
            placeholder="mr.scope"
            onChange={(event) => setDraft({ ...draft, scope: event.target.value })}
          />
          <input
            value={draft.title}
            placeholder="Название"
            onChange={(event) => setDraft({ ...draft, title: event.target.value })}
          />
          <input
            value={draft.sources}
            placeholder="Источники через запятую"
            onChange={(event) => setDraft({ ...draft, sources: event.target.value })}
          />
          <textarea
            value={draft.statement}
            placeholder="Формулировка правила"
            onChange={(event) => setDraft({ ...draft, statement: event.target.value })}
          />
          <button
            className="primary"
            disabled={!draft.id || !draft.title || !draft.statement || !draft.sources}
            onClick={create}
          >
            Добавить правило
          </button>
        </div>
        {error && <p className="knowledge-error">{error}</p>}
        <div className="rule-list">
          {shown.map((rule) => (
            <article key={rule.id} className={`rule-card rule-${rule.status}`}>
              <header>
                <div>
                  <code>{rule.id}</code>
                  <h2>{rule.title}</h2>
                  <small>{rule.scope} · v{rule.version} · {rule.confidence}</small>
                </div>
                <select
                  value={rule.status}
                  onChange={(event) =>
                    setStatus(rule, event.target.value as RuleDefinition["status"])
                  }
                >
                  <option value="confirmed">подтверждено</option>
                  <option value="candidate">кандидат</option>
                  <option value="disputed">есть расхождения</option>
                  <option value="unknown">не определено</option>
                  <option value="retired">выведено из использования</option>
                </select>
              </header>
              <p>{rule.statement}</p>
              <footer>
                <span>{rule.sources.join(" · ")}</span>
                {rule.test_notes && <em>{rule.test_notes}</em>}
              </footer>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

type EditorMode = "create" | "edit" | "password";

function HistoricalData({
  token,
  onClose,
}: {
  token: string;
  onClose: () => void;
}) {
  const [datasets, setDatasets] = useState<HistoricalDataset[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [bars, setBars] = useState<Bar[]>([]);
  const [offset, setOffset] = useState(0);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [inspection, setInspection] = useState<OhlcInspection | null>(null);
  const [serverFiles, setServerFiles] = useState<ServerDataFile[]>([]);
  const [error, setError] = useState("");
  const [quality, setQuality] = useState<DatasetQualityReport | null>(null);
  const [coverage, setCoverage] = useState<MarketCoverageReport | null>(null);
  const [issues, setIssues] = useState<DataIssueReport | null>(null);
  const [aggregationMatrix, setAggregationMatrix] = useState<AggregationParityReport | null>(null);
  const [refreshPlan, setRefreshPlan] = useState<RefreshPlanReport | null>(null);
  const [instrumentRegistry, setInstrumentRegistry] = useState<InstrumentIdentity[]>([]);
  const [calendarProfiles, setCalendarProfiles] = useState<CalendarProfile[]>([]);
  const [researchUniverse, setResearchUniverse] = useState<ResearchUniverseReport | null>(null);
  const [versionChain, setVersionChain] = useState<VersionChainReport | null>(null);
  const [datasetFilter, setDatasetFilter] = useState("");
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [comparisonSeriesId, setComparisonSeriesId] = useState("");
  const [timeframeComparison, setTimeframeComparison] = useState<TimeframeComparisonReport | null>(null);
  const [metadataDraft, setMetadataDraft] = useState({
    evidence_tier: "experimental" as HistoricalDataset["evidence_tier"],
    provenance_ref: "", timezone_name: "UTC", source_issues: "",
  });

  const loadDatasets = useCallback(async () => {
    const response = await authorizedFetch(token, "/api/history");
    if (!response.ok) throw new Error("Не удалось загрузить каталог данных");
    const payload = (await response.json()) as HistoricalDataset[];
    setDatasets(payload);
    setSelectedId((current) =>
      payload.some((item) => item.id === current) ? current : (payload[0]?.id ?? ""),
    );
  }, [token]);

  const loadServerFiles = async () => {
    const response = await authorizedFetch(token, "/api/history/server-files");
    if (!response.ok) throw new Error("Не удалось прочитать файлы сервера");
    setServerFiles((await response.json()) as ServerDataFile[]);
  };

  const loadCoverage = async () => {
    const response = await authorizedFetch(token, "/api/history/coverage");
    if (!response.ok) throw new Error("Не удалось построить реестр покрытия");
    setCoverage((await response.json()) as MarketCoverageReport);
  };

  const loadDataControl = async () => {
    const [issueResponse, matrixResponse, planResponse, instrumentsResponse, calendarsResponse, universeResponse] = await Promise.all([
      authorizedFetch(token, "/api/history/issues"), authorizedFetch(token, "/api/history/aggregation-matrix"),
      authorizedFetch(token, "/api/history/refresh-plan"), authorizedFetch(token, "/api/history/instruments"),
      authorizedFetch(token, "/api/history/calendar-profiles"),
      authorizedFetch(token, "/api/history/research-universe"),
    ]);
    if (![issueResponse, matrixResponse, planResponse, instrumentsResponse, calendarsResponse, universeResponse].every((response) => response.ok)) {
      throw new Error("Не удалось загрузить контроль данных");
    }
    setIssues((await issueResponse.json()) as DataIssueReport);
    setAggregationMatrix((await matrixResponse.json()) as AggregationParityReport);
    setRefreshPlan((await planResponse.json()) as RefreshPlanReport);
    setInstrumentRegistry((await instrumentsResponse.json()) as InstrumentIdentity[]);
    setCalendarProfiles((await calendarsResponse.json()) as CalendarProfile[]);
    setResearchUniverse((await universeResponse.json()) as ResearchUniverseReport);
  };

  const compareTimeframes = async () => {
    if (!selectedId || !comparisonSeriesId) return;
    const response = await authorizedFetch(token, `/api/history/${selectedId}/compare-timeframe?other_series_id=${comparisonSeriesId}`);
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Не удалось сравнить таймфреймы");
      return;
    }
    setTimeframeComparison((await response.json()) as TimeframeComparisonReport);
  };

  const loadVersionChain = async () => {
    if (!selectedId) return;
    const response = await authorizedFetch(token, `/api/history/${selectedId}/version-chain`);
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Не удалось прочитать цепочку версий");
      return;
    }
    setVersionChain((await response.json()) as VersionChainReport);
  };

  const composeVersionChain = async () => {
    if (!selectedId || !versionChain?.composable || versionChain.segments.length < 2) return;
    const response = await authorizedFetch(token, `/api/history/${selectedId}/version-chain/compose`, { method: "POST" });
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Не удалось собрать исследовательский ряд");
      return;
    }
    const dataset = (await response.json()) as HistoricalDataset;
    setSelectedId(dataset.id); setOffset(0); await loadDatasets(); await loadCoverage();
  };

  const importServerFile = async (file: ServerDataFile) => {
    const params = new URLSearchParams({ relative_path: file.path, symbol: file.symbol || "AUTO", provider: file.provider || "server file", interval: file.interval || "auto", exchange: file.exchange || "local", category: file.category || "research" });
    const response = await authorizedFetch(token, `/api/history/import/server-file?${params}`, { method: "POST" });
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Не удалось зарегистрировать файл");
      return;
    }
    const dataset = (await response.json()) as HistoricalDataset;
    setSelectedId(dataset.id); setOffset(0); await loadDatasets();
  };

  useEffect(() => {
    loadDatasets().catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : "Ошибка загрузки"),
    );
  }, [loadDatasets]);

  useEffect(() => {
    loadCoverage().catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : "Ошибка реестра покрытия"),
    );
  }, [token]);

  useEffect(() => {
    if (!selectedId) {
      setBars([]);
      return;
    }
    authorizedFetch(token, `/api/history/${selectedId}/bars?offset=${offset}&limit=100`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Не удалось открыть данные");
        setBars((await response.json()) as Bar[]);
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Ошибка просмотра"),
      );
  }, [offset, selectedId, token]);

  useEffect(() => {
    if (!selectedId) {
      setQuality(null);
      return;
    }
    authorizedFetch(token, `/api/history/${selectedId}/quality`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Не удалось проверить качество ряда");
        setQuality((await response.json()) as DatasetQualityReport);
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Ошибка проверки"),
      );
  }, [selectedId, token]);

  const chooseFile = async (file: File) => {
    setError("");
    const response = await authorizedFetch(token, "/api/history/import/file/inspect", {
      method: "POST",
      headers: { "content-type": "application/octet-stream" },
      body: file,
    });
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Не удалось разобрать файл");
      return;
    }
    setPendingFile(file);
    setInspection((await response.json()) as OhlcInspection);
  };

  const remove = async (dataset: HistoricalDataset) => {
    if (!window.confirm(`Удалить набор ${dataset.symbol} (${dataset.provider})?`)) return;
    const response = await authorizedFetch(token, `/api/history/${dataset.id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Не удалось удалить набор");
      return;
    }
    setOffset(0);
    await loadDatasets();
  };

  const selected = datasets.find((item) => item.id === selectedId);
  const shownDatasets = datasets.filter((item) => {
    const query = datasetFilter.trim().toLowerCase();
    const textMatch = !query || `${item.symbol} ${item.interval} ${item.provider} ${item.exchange}`.toLowerCase().includes(query);
    const priorityMatch = !priorityOnly || ["BTCUSDT", "ETHUSDT", "XRPUSDT", "SOLUSDT", "DOGEUSDT", "USDRUB", "RUBCNY", "XAUUSD", "SPX", "WTI"].includes(item.symbol);
    const issueMatch = !issuesOnly || !!issues?.issues.some((issue) => issue.series_id === item.id);
    return textMatch && priorityMatch && issueMatch;
  });
  useEffect(() => {
    if (!selected) return;
    setMetadataDraft({
      evidence_tier: selected.evidence_tier,
      provenance_ref: selected.provenance_ref,
      timezone_name: selected.timezone_name,
      source_issues: selected.source_issues,
    });
  }, [selectedId, selected?.evidence_tier, selected?.provenance_ref, selected?.timezone_name, selected?.source_issues]);

  const saveMetadata = async () => {
    if (!selected) return;
    const response = await authorizedFetch(token, `/api/history/${selected.id}`, {
      method: "PATCH", headers: { "content-type": "application/json" },
      body: JSON.stringify(metadataDraft),
    });
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Не удалось сохранить provenance");
      return;
    }
    const updated = (await response.json()) as HistoricalDataset;
    setDatasets((current) => current.map((item) => item.id === updated.id ? updated : item));
  };
  return (
    <div className="admin-overlay history-overlay">
      <section className="admin-page history-page">
        <header>
          <div>
            <span>ДАННЫЕ ДЛЯ ИССЛЕДОВАНИЙ</span>
            <h2>Исторические данные для обучения</h2>
          </div>
          <div>
            <button className="compact" onClick={() => void loadCoverage()}>Реестр покрытия</button>
            <button className="compact" onClick={() => void loadDataControl().catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Ошибка контроля данных"))}>Контроль данных</button>
            <button className="compact" onClick={() => void loadServerFiles()}>Файлы сервера</button>
            <label className="primary compact file-button">
              Загрузить файл
              <input
                type="file"
                accept=".csv,.txt,.tsv"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void chooseFile(file);
                  event.target.value = "";
                }}
              />
            </label>
            <button className="close-button" onClick={onClose}>Закрыть</button>
          </div>
        </header>
        {error && <p className="form-error">{error}</p>}
        {!!serverFiles.length && (
          <div className="server-files">
            {serverFiles.map((file) => <button key={file.path} onClick={() => void importServerFile(file)}>Зарегистрировать: {file.path} ({Math.ceil(file.size_bytes / 1024)} KiB)</button>)}
          </div>
        )}
        {coverage && (
          <div className="dataset-quality">
            <b>Реестр покрытия: {coverage.counts.series} рядов · нативные/импорт: {coverage.counts.native_or_imported} · агрегированные: {coverage.counts.aggregated}</b>
            <small>{coverage.notice}</small>
            {coverage.rows.map((row) => <small key={row.series_id}>{row.symbol} · {row.interval} · {row.provider} · {row.origin} · баров {row.bar_count} · пропуски {row.gap_count}{row.metadata_missing.length ? ` · нет: ${row.metadata_missing.join(", ")}` : ""}</small>)}
          </div>
        )}
        {(issues || aggregationMatrix || refreshPlan || instrumentRegistry.length || calendarProfiles.length || researchUniverse) && (
          <div className="dataset-quality">
            <b>Контроль данных</b>
            {issues && <small>Очередь проблем: всего {issues.counts.total || 0}; высоких {issues.counts.high || 0}; средних {issues.counts.medium || 0}. {issues.notice}</small>}
            {issues?.issues.slice(0, 12).map((item) => <small key={`${item.code}-${item.series_id}`}>[{item.severity}] {item.symbol} · {item.interval}: {item.message} Действие: {item.action}</small>)}
            {aggregationMatrix && <small>Матрица ТФ: агрегированных {aggregationMatrix.counts.derived || 0}; совпало с нативом {aggregationMatrix.counts.equal || 0}; различий {aggregationMatrix.counts.differences_found || 0}; без натива {aggregationMatrix.counts.native_not_available || 0}.</small>}
            {aggregationMatrix?.rows.slice(0, 12).map((item) => <small key={item.derived_series_id}>{item.symbol} · {item.interval}: {item.state}{item.comparison ? ` · OHLCV-расхождений ${item.comparison.ohlc_mismatch_count}` : ""}.</small>)}
            {refreshPlan && <small>План обновления: {refreshPlan.items.filter((item) => item.action === "provider_incremental_download").length} рядов с адаптером; обновление всегда создаёт новый файл.</small>}
            {refreshPlan?.items.slice(0, 12).map((item) => <small key={item.series_id}>{item.symbol} · {item.interval} · {item.provider}: {item.action} — {item.reason}</small>)}
            {researchUniverse && <small>Приоритетный контур: покрыто {researchUniverse.counts.complete || 0}; частично {researchUniverse.counts.partial || 0}; пусто {researchUniverse.counts.empty || 0}.</small>}
            {researchUniverse?.rows.map((item) => <small key={item.symbol}>{item.symbol}: {item.status}; есть {item.available_intervals.join(", ") || "—"}; нужны {item.missing_intervals.join(", ") || "—"}.</small>)}
            {!!instrumentRegistry.length && <small>Идентичность: {instrumentRegistry.filter((item) => item.substitutions_forbidden.length).map((item) => `${item.symbol} ≠ ${item.substitutions_forbidden.join(", ")}`).join("; ")}.</small>}
            {!!calendarProfiles.length && <small>Календарные профили: {calendarProfiles.map((item) => `${item.id}/${item.version} (${item.aggregation_profile})`).join("; ")}.</small>}
          </div>
        )}
        <div className="dataset-provenance">
          <label>Фильтр рядов
            <input value={datasetFilter} placeholder="инструмент, ТФ, источник" onChange={(event) => setDatasetFilter(event.target.value)} />
          </label>
          <label><input type="checkbox" checked={priorityOnly} onChange={(event) => setPriorityOnly(event.target.checked)} /> Только приоритетные рынки</label>
          <label><input type="checkbox" checked={issuesOnly} onChange={(event) => setIssuesOnly(event.target.checked)} /> Только с проблемами</label>
          <small>Показано {shownDatasets.length} из {datasets.length} рядов.</small>
        </div>
        <div className="history-layout">
          <aside className="history-list">
            {shownDatasets.map((dataset) => (
              <button
                key={dataset.id}
                className={dataset.id === selectedId ? "selected" : ""}
                onClick={() => {
                  setSelectedId(dataset.id);
                  setOffset(0);
                }}
              >
                <strong>{dataset.symbol} · {dataset.interval}</strong>
                <span>{dataset.provider} · {dataset.bar_count} баров</span>
                <small>{evidenceTierLabel[dataset.evidence_tier]} · {new Date(dataset.uploaded_at).toLocaleString("ru-RU")}</small>
              </button>
            ))}
            {!shownDatasets.length && <p>Под фильтр не попало ни одного набора.</p>}
          </aside>
          <article className="history-viewer">
            {!selected && <p>Выберите набор данных.</p>}
            {selected && (
              <>
                <div className="history-summary">
                  <div>
                    <h3>{selected.symbol}</h3>
                    <p>{selected.note}</p>
                    <small>
                      Файл: {selected.original_filename || "не указан"} · SHA-256: {selected.sha256}
                    </small>
                    <div className="dataset-provenance">
                      <label>Уровень
                        <select value={metadataDraft.evidence_tier} onChange={(event) => setMetadataDraft({ ...metadataDraft, evidence_tier: event.target.value as HistoricalDataset["evidence_tier"] })}>
                          <option value="author_canonical">канон автора</option>
                          <option value="source_anchored">локально подтверждённый</option>
                          <option value="experimental">исследовательский</option>
                        </select>
                      </label>
                      <label>Происхождение
                        <input value={metadataDraft.provenance_ref} placeholder="путь, URL, урок, хеш" onChange={(event) => setMetadataDraft({ ...metadataDraft, provenance_ref: event.target.value })} />
                      </label>
                      <label>Часовой пояс
                        <input value={metadataDraft.timezone_name} onChange={(event) => setMetadataDraft({ ...metadataDraft, timezone_name: event.target.value })} />
                      </label>
                      <label>Неопределённости
                        <input value={metadataDraft.source_issues} placeholder="неизвестный provider, adjustment…" onChange={(event) => setMetadataDraft({ ...metadataDraft, source_issues: event.target.value })} />
                      </label>
                      <button onClick={() => void saveMetadata()}>Сохранить provenance</button>
                    </div>
                    {quality && (
                      <div className={`dataset-quality ${quality.chronology_violations || quality.duplicate_open_times || quality.known_at_violations ? "quality-error" : ""}`}>
                        <b>Контроль целостности</b>
                        <span>баров: {quality.bar_count}; пропусков по номинальному ТФ: {quality.gap_count}; largest gap: {quality.largest_gap_ms || 0} мс</span>
                        <span>нарушения порядка: {quality.chronology_violations}; дубли: {quality.duplicate_open_times}; known_at: {quality.known_at_violations}</span>
                        {!!quality.metadata_missing.length && <span>не заполнено: {quality.metadata_missing.join(", ")}</span>}
                        {quality.source_issues && <span>помеченные неопределённости: {quality.source_issues}</span>}
                        <small>{quality.notice}</small>
                      </div>
                    )}
                    <div className="dataset-provenance">
                      <label>Сравнить с рядом того же ТФ
                        <select value={comparisonSeriesId} onChange={(event) => setComparisonSeriesId(event.target.value)}>
                          <option value="">Выберите ряд</option>
                          {datasets.filter((item) => item.id !== selected.id && item.interval === selected.interval).map((item) => <option key={item.id} value={item.id}>{item.symbol} · {item.provider}</option>)}
                        </select>
                      </label>
                      <button disabled={!comparisonSeriesId} onClick={() => void compareTimeframes()}>Сверить бары</button>
                      {timeframeComparison && <small>Совпало: {timeframeComparison.matched_bars}; только в текущем: {timeframeComparison.base_only_bars}; только в сравнительном: {timeframeComparison.other_only_bars}; расхождений OHLCV: {timeframeComparison.ohlc_mismatch_count}{timeframeComparison.first_mismatch ? ` · первое: ${new Date(timeframeComparison.first_mismatch.open_time_ms).toLocaleString("ru-RU")}` : ""}. Не выбирает «правильный» источник.</small>}
                    </div>
                    <div className="dataset-provenance">
                      <button onClick={() => void loadVersionChain()}>Цепочка версий</button>
                      {versionChain && <small>Сегментов: {versionChain.segments.length}; {versionChain.composable ? "можно собрать исследовательский ряд" : "сборка заблокирована"}. {versionChain.notice}</small>}
                      {versionChain && versionChain.segments.length > 1 && <button disabled={!versionChain.composable} onClick={() => void composeVersionChain()}>Собрать исследовательский ряд</button>}
                    </div>
                  </div>
                  <button className="danger" onClick={() => void remove(selected)}>
                    Удалить
                  </button>
                </div>
                <div className="history-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Дата/время UTC</th><th>Открытие</th><th>Максимум</th>
                        <th>Минимум</th><th>Закрытие</th><th>Объём</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bars.map((bar) => (
                        <tr key={bar.open_time_ms}>
                          <td>{new Date(bar.open_time_ms).toLocaleString("ru-RU", { timeZone: "UTC" })}</td>
                          <td>{bar.open}</td><td>{bar.high}</td><td>{bar.low}</td>
                          <td>{bar.close}</td><td>{bar.volume}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="history-pager">
                  <button
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - 100))}
                  >Назад</button>
                  <span>{offset + 1}–{offset + bars.length} из {selected.bar_count}</span>
                  <button
                    disabled={offset + bars.length >= selected.bar_count}
                    onClick={() => setOffset(offset + 100)}
                  >Далее</button>
                </div>
              </>
            )}
          </article>
        </div>
      </section>
      {pendingFile && inspection && (
        <OhlcImportDialog
          token={token}
          file={pendingFile}
          inspection={inspection}
          historyMode
          onClose={() => {
            setPendingFile(null);
            setInspection(null);
          }}
          onImported={(dataset) => {
            setPendingFile(null);
            setInspection(null);
            setSelectedId(dataset.id);
            setOffset(0);
            void loadDatasets();
          }}
        />
      )}
    </div>
  );
}

function SimulationSettings({
  token,
  onClose,
}: {
  token: string;
  onClose: () => void;
}) {
  const [profile, setProfile] = useState<SimulationProfile | null>(null);
  const [draft, setDraft] = useState<SimulationPolicyView | null>(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const response = await authorizedFetch(token, "/api/bot-readiness/simulation-policy");
    if (!response.ok) throw new Error("Не удалось загрузить параметры виртуального эксперимента");
    const next = (await response.json()) as SimulationProfile;
    setProfile(next);
    setDraft(next.policy);
  }, [token]);

  useEffect(() => {
    load().catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : "Ошибка загрузки"),
    );
  }, [load]);

  const setValue = <K extends keyof SimulationPolicyView>(key: K, value: SimulationPolicyView[K]) => {
    setDraft((current) => current ? { ...current, [key]: value } : current);
  };

  const restoreDefaults = () => {
    if (!draft) return;
    setDraft({
      ...draft,
      initial_balance_usdt: "100.00",
      fee_bps: "10",
      slippage_bps: "5",
      max_open_positions: 1,
      max_notional_usdt: "25.00",
      max_risk_fraction: "0.01",
      max_daily_loss_usdt: "3.00",
      cooldown_after_rejections: 3,
      max_data_staleness_seconds: 90,
      email_notifications_enabled: false,
      telegram_notifications_enabled: false,
    });
    setMessage("Консервативные значения подставлены. Нажмите «Сохранить», чтобы применить их к черновику.");
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError("");
    setMessage("");
    const response = await authorizedFetch(token, "/api/bot-readiness/simulation-policy", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        initial_balance_usdt: draft.initial_balance_usdt,
        fee_bps: draft.fee_bps,
        slippage_bps: draft.slippage_bps,
        same_bar_resolution: draft.same_bar_resolution,
        max_open_positions: draft.max_open_positions,
        max_notional_usdt: draft.max_notional_usdt,
        max_risk_fraction: draft.max_risk_fraction,
        max_daily_loss_usdt: draft.max_daily_loss_usdt,
        cooldown_after_rejections: draft.cooldown_after_rejections,
        max_data_staleness_seconds: draft.max_data_staleness_seconds,
        email_notifications_enabled: draft.email_notifications_enabled,
        telegram_notifications_enabled: draft.telegram_notifications_enabled,
      }),
    });
    setBusy(false);
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Параметры не сохранены");
      return;
    }
    const saved = (await response.json()) as SimulationProfile;
    setProfile(saved);
    setDraft(saved.policy);
    setMessage("Черновик сохранён. Права на симуляцию и торговлю не изменены.");
  };

  if (!profile || !draft) {
    return (
      <div className="admin-overlay">
        <section className="admin-page simulation-page"><p className="simulation-loading">Загрузка параметров виртуального эксперимента…</p></section>
      </div>
    );
  }

  return (
    <div className="admin-overlay">
      <section className="admin-page simulation-page">
        <header>
          <div>
            <span>ВИРТУАЛЬНЫЙ ЭКСПЕРИМЕНТ</span>
            <h2>Параметры и защита</h2>
          </div>
          <div>
            <button className="compact" onClick={restoreDefaults}>Консервативные значения</button>
            <button className="close-button" onClick={onClose}>Закрыть</button>
          </div>
        </header>
        {error && <p className="form-error">{error}</p>}
        {message && <p className="simulation-message">{message}</p>}
        <div className="simulation-warning">
          <b>Сделки не запущены.</b> Режим: research-only; trade_permission={profile.trade_permission}; simulation_permission={profile.simulation_permission}.
          Сохранение меняет только неисполняемый черновик на сервере.
        </div>
        <div className="simulation-grid">
          <section>
            <h3>Экономика каждого инструмента</h3>
            <p>Баланс, риск и дневной лимит ведутся раздельно для каждой пары; общий баланс между инструментами не используется.</p>
            <label>Начальный баланс на инструмент, USDT
              <input type="number" min="0.01" step="0.01" value={draft.initial_balance_usdt} onChange={(event) => setValue("initial_balance_usdt", event.target.value)} />
            </label>
            <label>Максимум виртуальных позиций на инструмент
              <input type="number" min="1" max="10" step="1" value={draft.max_open_positions} onChange={(event) => setValue("max_open_positions", Number(event.target.value))} />
            </label>
            <label>Максимальный объём одной позиции, USDT
              <input type="number" min="0.01" step="0.01" value={draft.max_notional_usdt} onChange={(event) => setValue("max_notional_usdt", event.target.value)} />
            </label>
            <label>Риск на позицию, доля баланса
              <input type="number" min="0.0001" max="1" step="0.0001" value={draft.max_risk_fraction} onChange={(event) => setValue("max_risk_fraction", event.target.value)} />
            </label>
            <label>Дневной лимит виртуального убытка, USDT
              <input type="number" min="0.01" step="0.01" value={draft.max_daily_loss_usdt} onChange={(event) => setValue("max_daily_loss_usdt", event.target.value)} />
            </label>
            <label>Пауза после отклонений, событий
              <input type="number" min="0" max="100" step="1" value={draft.cooldown_after_rejections} onChange={(event) => setValue("cooldown_after_rejections", Number(event.target.value))} />
            </label>
          </section>
          <section>
            <h3>Издержки и защитный стоп</h3>
            <label>Комиссия, б.п.
              <input type="number" min="0" step="0.1" value={draft.fee_bps} onChange={(event) => setValue("fee_bps", event.target.value)} />
            </label>
            <label>Проскальзывание, б.п.
              <input type="number" min="0" step="0.1" value={draft.slippage_bps} onChange={(event) => setValue("slippage_bps", event.target.value)} />
            </label>
            <label>Максимальная давность данных, секунд
              <input type="number" min="15" max="3600" step="1" value={draft.max_data_staleness_seconds} onChange={(event) => setValue("max_data_staleness_seconds", Number(event.target.value))} />
            </label>
            <div className="simulation-fixed">
              <b>Обязательные правила, не редактируются</b>
              <span>Стоп: уровень структурной отмены соответствующего паттерна.</span>
              <span>Если цель и стоп достигнуты в одной свече, первым считается стоп.</span>
              <span>При потере свежести данных новые входы замораживаются; открытая виртуальная позиция требует консервативной сверки после восстановления.</span>
            </div>
          </section>
          <section>
            <h3>Уведомления о критических событиях</h3>
            <p>Сохраняются предпочтения каналов. Пока исполнителя нет, сообщения не отправляются.</p>
            <label className="checkbox-label">
              <input type="checkbox" checked={draft.email_notifications_enabled} onChange={(event) => setValue("email_notifications_enabled", event.target.checked)} />
              Email: уведомлять после подключения SMTP
            </label>
            <label className="checkbox-label">
              <input type="checkbox" checked={draft.telegram_notifications_enabled} onChange={(event) => setValue("telegram_notifications_enabled", event.target.checked)} />
              Telegram: уведомлять после защищённой настройки бота
            </label>
            <div className="simulation-fixed">
              <b>Поводы для тревоги</b>
              {profile.critical_events_ru.map((item) => <span key={item}>{item}</span>)}
            </div>
            <small>{profile.notification_readiness_ru.email}</small>
            <small>{profile.notification_readiness_ru.telegram}</small>
          </section>
        </div>
        <footer className="simulation-footer">
          <span>{profile.updated_at_utc ? `Последнее изменение: ${new Date(profile.updated_at_utc).toLocaleString("ru-RU")} (${profile.updated_by || "не указан"})` : "Сохранённого профиля ещё нет."}</span>
          <button className="primary compact" disabled={busy} onClick={() => void save()}>{busy ? "Сохранение…" : "Сохранить черновик"}</button>
        </footer>
      </section>
    </div>
  );
}

function UsersAdmin({
  token,
  currentUser,
  onClose,
}: {
  token: string;
  currentUser: User;
  onClose: () => void;
}) {
  const emptyForm = {
    username: "",
    email: "",
    full_name: "",
    password: "",
    is_active: true,
  };
  const [users, setUsers] = useState<User[]>([]);
  const [mode, setMode] = useState<EditorMode | null>(null);
  const [target, setTarget] = useState<User | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");

  const loadUsers = useCallback(async () => {
    const response = await authorizedFetch(token, "/api/users");
    if (!response.ok) throw new Error("Не удалось загрузить пользователей");
    setUsers((await response.json()) as User[]);
  }, [token]);

  useEffect(() => {
    loadUsers().catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : "Ошибка"),
    );
  }, [loadUsers]);

  const openCreate = () => {
    setTarget(null);
    setForm(emptyForm);
    setError("");
    setMode("create");
  };

  const openEdit = (user: User) => {
    setTarget(user);
    setForm({
      username: user.username,
      email: user.email ?? "",
      full_name: user.full_name,
      password: "",
      is_active: user.is_active,
    });
    setError("");
    setMode("edit");
  };

  const openPassword = (user: User) => {
    setTarget(user);
    setForm({ ...emptyForm, password: "" });
    setError("");
    setMode("password");
  };

  const save = async () => {
    setError("");
    let url = "/api/users";
    let method = "POST";
    let body: Record<string, unknown> = {
      username: form.username,
      email: form.email || null,
      full_name: form.full_name,
      password: form.password,
      is_active: form.is_active,
    };
    if (mode === "edit" && target) {
      url = `/api/users/${target.id}`;
      method = "PATCH";
      body = {
        username: form.username,
        email: form.email || null,
        full_name: form.full_name,
        is_active: form.is_active,
      };
    } else if (mode === "password" && target) {
      url = `/api/users/${target.id}/password`;
      method = "POST";
      body = { password: form.password };
    }
    const response = await authorizedFetch(token, url, {
      method,
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || `HTTP ${response.status}`);
      return;
    }
    setMode(null);
    await loadUsers();
  };

  const toggleBlocked = async (user: User) => {
    const response = await authorizedFetch(token, `/api/users/${user.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ is_active: !user.is_active }),
    });
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Операция отклонена");
      return;
    }
    await loadUsers();
  };

  const remove = async (user: User) => {
    if (!window.confirm(`Удалить пользователя ${user.username}?`)) return;
    const response = await authorizedFetch(token, `/api/users/${user.id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setError(payload.detail || "Удаление отклонено");
      return;
    }
    await loadUsers();
  };

  return (
    <div className="admin-overlay">
      <section className="admin-page">
        <header>
          <div>
            <span>АДМИНИСТРИРОВАНИЕ</span>
            <h2>Пользователи</h2>
          </div>
          <div>
            <button className="primary compact" onClick={openCreate}>
              Добавить
            </button>
            <button className="close-button" onClick={onClose}>
              Закрыть
            </button>
          </div>
        </header>
        {error && <p className="form-error">{error}</p>}
        <div className="users-table">
          <div className="users-row users-head">
            <span>Пользователь</span>
            <span>Email</span>
            <span>Состояние</span>
            <span>Действия</span>
          </div>
          {users.map((user) => (
            <div className="users-row" key={user.id}>
              <span>
                <b>{user.username}</b>
                <small>{user.full_name}</small>
              </span>
              <span>{user.email || "не указан"}</span>
              <span className={user.is_active ? "user-active" : "user-blocked"}>
                {user.is_active ? "активен" : "заблокирован"}
              </span>
              <span className="row-actions">
                <button onClick={() => openEdit(user)}>Изменить</button>
                <button
                  onClick={() => toggleBlocked(user)}
                  disabled={user.id === currentUser.id}
                >
                  {user.is_active ? "Блокировать" : "Разблокировать"}
                </button>
                <button onClick={() => openPassword(user)}>Пароль</button>
                <button
                  className="danger"
                  onClick={() => remove(user)}
                  disabled={user.id === currentUser.id}
                >
                  Удалить
                </button>
              </span>
            </div>
          ))}
        </div>
      </section>

      {mode && (
        <div className="modal-backdrop">
          <section className="user-modal">
            <span>
              {mode === "create"
                ? "НОВЫЙ ПОЛЬЗОВАТЕЛЬ"
                : mode === "edit"
                  ? "РЕДАКТИРОВАНИЕ"
                  : "НОВЫЙ ПАРОЛЬ"}
            </span>
            <h3>
              {mode === "create"
                ? "Добавить пользователя"
                : target?.username}
            </h3>
            {mode !== "password" && (
              <>
                <label>
                  Логин
                  <input
                    value={form.username}
                    onChange={(event) =>
                      setForm({ ...form, username: event.target.value })
                    }
                  />
                </label>
                <label>
                  Имя
                  <input
                    value={form.full_name}
                    onChange={(event) =>
                      setForm({ ...form, full_name: event.target.value })
                    }
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) =>
                      setForm({ ...form, email: event.target.value })
                    }
                  />
                </label>
              </>
            )}
            {(mode === "create" || mode === "password") && (
              <label>
                Пароль
                <input
                  type="password"
                  minLength={10}
                  value={form.password}
                  onChange={(event) =>
                    setForm({ ...form, password: event.target.value })
                  }
                />
                <small>Не менее 10 символов</small>
              </label>
            )}
            {mode !== "password" && (
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(event) =>
                    setForm({ ...form, is_active: event.target.checked })
                  }
                />
                Активен
              </label>
            )}
            {error && <p className="form-error">{error}</p>}
            <div className="modal-actions">
              <button onClick={() => setMode(null)}>Отмена</button>
              <button className="primary compact" onClick={save}>
                Сохранить
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function Login({
  onAuthenticated,
}: {
  onAuthenticated: (token: string) => void;
}) {
  const [forgot, setForgot] = useState(false);
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submitLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (response.ok) {
      const payload = (await response.json()) as { access_token: string };
      onAuthenticated(payload.access_token);
    } else {
      const payload = (await response.json()) as { detail?: string };
      setMessage(payload.detail || "Ошибка входа");
    }
    setBusy(false);
  };

  const submitForgot = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const response = await fetch("/api/auth/forgot-password", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (response.ok) {
      const payload = (await response.json()) as {
        delivery_configured: boolean;
      };
      setMessage(
        payload.delivery_configured
          ? "Если email зарегистрирован, ссылка отправлена."
          : "Отправка email пока не настроена. Обратитесь к администратору.",
      );
    } else {
      setMessage("Проверьте адрес email");
    }
    setBusy(false);
  };

  return (
    <main className="auth-shell">
      <section className="auth-card">
        <span className="auth-logo">A</span>
        <small>ADVERSA / ЧАСТНАЯ ИССЛЕДОВАТЕЛЬСКАЯ ЛАБОРАТОРИЯ</small>
        <h1>{forgot ? "Сброс пароля" : "Вход в систему"}</h1>
        <p>
          {forgot
            ? "Укажите email пользователя. Ссылка действует 30 минут."
            : "Доступ к графикам и данным разрешён только авторизованным пользователям."}
        </p>
        <form onSubmit={forgot ? submitForgot : submitLogin}>
          {forgot ? (
            <label>
              Email
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
          ) : (
            <>
              <label>
                Логин
                <input
                  required
                  autoFocus
                  autoComplete="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                />
              </label>
              <label>
                Пароль
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>
            </>
          )}
          {message && <p className="auth-message">{message}</p>}
          <button className="primary" disabled={busy}>
            {busy ? "Подождите…" : forgot ? "Отправить ссылку" : "Войти"}
          </button>
        </form>
        <button
          className="text-button"
          onClick={() => {
            setForgot((value) => !value);
            setMessage("");
          }}
        >
          {forgot ? "Вернуться ко входу" : "Забыли пароль?"}
        </button>
      </section>
    </main>
  );
}

function ResetPassword({
  token,
  onComplete,
}: {
  token: string;
  onComplete: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState("");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setMessage("Пароли не совпадают");
      return;
    }
    const response = await fetch("/api/auth/reset-password", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    if (!response.ok) {
      const payload = (await response.json()) as { detail?: string };
      setMessage(payload.detail || "Не удалось изменить пароль");
      return;
    }
    setMessage("Пароль изменён");
    window.setTimeout(onComplete, 900);
  };

  return (
    <main className="auth-shell">
      <section className="auth-card">
        <span className="auth-logo">A</span>
        <small>ADVERSA / СБРОС ПАРОЛЯ</small>
        <h1>Новый пароль</h1>
        <form onSubmit={submit}>
          <label>
            Пароль
            <input
              type="password"
              minLength={10}
              required
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <label>
            Повторите пароль
            <input
              type="password"
              minLength={10}
              required
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </label>
          {message && <p className="auth-message">{message}</p>}
          <button className="primary">Сохранить пароль</button>
        </form>
      </section>
    </main>
  );
}

function App() {
  const resetToken = new URLSearchParams(window.location.search).get(
    "reset_token",
  );
  const [token, setToken] = useState(
    () => window.localStorage.getItem("adversa_token") || "",
  );
  const [currentUser, setCurrentUser] = useState<User | null | undefined>(
    undefined,
  );
  const [usersOpen, setUsersOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [researchContractsOpen, setResearchContractsOpen] = useState(false);
  const [researchReadinessOpen, setResearchReadinessOpen] = useState(false);
  const [researchObservationsOpen, setResearchObservationsOpen] = useState(false);
  const [simulationSettingsOpen, setSimulationSettingsOpen] = useState(false);

  const logout = useCallback(() => {
    window.localStorage.removeItem("adversa_token");
    setToken("");
    setCurrentUser(null);
    setUsersOpen(false);
    setKnowledgeOpen(false);
    setHistoryOpen(false);
    setRulesOpen(false);
    setResearchContractsOpen(false);
    setResearchReadinessOpen(false);
    setResearchObservationsOpen(false);
    setSimulationSettingsOpen(false);
  }, []);

  useEffect(() => {
    window.addEventListener("adversa:unauthorized", logout);
    return () => window.removeEventListener("adversa:unauthorized", logout);
  }, [logout]);

  useEffect(() => {
    if (!token) {
      setCurrentUser(null);
      return;
    }
    authorizedFetch(token, "/api/auth/me")
      .then(async (response) => {
        if (!response.ok) throw new Error("unauthorized");
        setCurrentUser((await response.json()) as User);
      })
      .catch(logout);
  }, [token, logout]);

  if (resetToken) {
    return (
      <ResetPassword
        token={resetToken}
        onComplete={() => {
          window.history.replaceState({}, "", "/");
          logout();
        }}
      />
    );
  }

  if (!token || currentUser === null) {
    return (
      <Login
        onAuthenticated={(nextToken) => {
          window.localStorage.setItem("adversa_token", nextToken);
          setToken(nextToken);
          setCurrentUser(undefined);
        }}
      />
    );
  }

  if (currentUser === undefined) {
    return (
      <main className="auth-shell">
        <section className="auth-card loading">Проверка сессии…</section>
      </main>
    );
  }

  return (
    <>
      <Lab
        token={token}
        currentUser={currentUser}
        onLogout={logout}
        onOpenUsers={() => setUsersOpen(true)}
        onOpenKnowledge={() => setKnowledgeOpen(true)}
        onOpenHistory={() => setHistoryOpen(true)}
        onOpenRules={() => setRulesOpen(true)}
        onOpenResearchContracts={() => setResearchContractsOpen(true)}
        onOpenResearchReadiness={() => setResearchReadinessOpen(true)}
        onOpenResearchObservations={() => setResearchObservationsOpen(true)}
        onOpenSimulationSettings={() => setSimulationSettingsOpen(true)}
      />
      {knowledgeOpen && (
        <KnowledgeBase token={token} onClose={() => setKnowledgeOpen(false)} />
      )}
      {historyOpen && (
        <HistoricalData token={token} onClose={() => setHistoryOpen(false)} />
      )}
      {rulesOpen && (
        <RuleRegistry token={token} onClose={() => setRulesOpen(false)} />
      )}
      {researchContractsOpen && (
        <ResearchContractCatalog token={token} onClose={() => setResearchContractsOpen(false)} />
      )}
      {researchReadinessOpen && (
        <ResearchReadiness token={token} onClose={() => setResearchReadinessOpen(false)} />
      )}
      {researchObservationsOpen && (
        <ResearchObservations token={token} onClose={() => setResearchObservationsOpen(false)} />
      )}
      {simulationSettingsOpen && (
        <SimulationSettings token={token} onClose={() => setSimulationSettingsOpen(false)} />
      )}
      {usersOpen && (
        <UsersAdmin
          token={token}
          currentUser={currentUser}
          onClose={() => setUsersOpen(false)}
        />
      )}
    </>
  );
}

export default App;
